<template>
  <div class="sales-dashboard">
    <!-- ===== 3D CUBE LOADER ===== -->
    <div v-if="isLoading" class="global-loader">
      <div class="loader-3d">
        <div class="cube">
          <div class="face front"></div>
          <div class="face back"></div>
          <div class="face right"></div>
          <div class="face left"></div>
          <div class="face top"></div>
          <div class="face bottom"></div>
        </div>
      </div>
      <p class="loader-text">Loading Sales Data...</p>
    </div>

    <!-- ===== HEADER ===== -->
    <div class="dashboard-header" v-if="!isLoading">
      <div class="header-content">
        <div class="header-left">
          <div class="header-icon">
            <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M3 3v18h18"/>
              <path d="M18 17V9"/>
              <path d="M13 17V5"/>
              <path d="M8 17v-3"/>
            </svg>
          </div>
          <div>
            <h1 class="header-title">Sales & Visits Report</h1>
            <p class="header-subtitle">Real-time analytics & performance metrics</p>
          </div>
        </div>
        <div class="header-right">
          <div class="header-badge">
            <span class="badge-dot"></span>
            <span>Live</span>
          </div>
          <div class="header-user">
            <b-icon-person-circle size="1.5rem" />
            <span>{{ userDetails.username || 'Guest' }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- ===== 6 FLIP CARDS ===== -->
    <div class="period-grid" v-if="!isLoading && periodCards.length">
      <div 
        v-for="period in periodCards" 
        :key="period.key"
        class="flip-card"
        :class="{ active: activePeriod === period.key, flipped: period.flipped }"
      >
        <div class="flip-card-inner">
          <!-- FRONT SIDE - Stats -->
          <div class="flip-card-front" @click="openPeriodModal(period.key)">
            <div class="flip-card-header">
              <span class="flip-label">{{ period.label }}</span>
              <span v-if="period.change !== null" 
                    class="period-change" 
                    :class="period.change >= 0 ? 'positive' : 'negative'">
                {{ period.change >= 0 ? '+' : '' }}{{ period.change.toFixed(1) }}%
              </span>
            </div>
            
            <div class="flip-main-stats">
              <div class="flip-stat">
                <span class="flip-stat-label">Total Sales</span><br>
                <span class="flip-stat-value primary">{{ formatCurrency(period.totalSales) }}</span>
              </div>
              <div class="flip-stat">
                <span class="flip-stat-label">Net Sales</span><br>
                <span class="flip-stat-value success">{{ formatCurrency(period.netSales) }}</span>
              </div>
              <div class="flip-stat spiff-stat">
                <span class="flip-stat-label">💰 SPIFF</span><br>
                <span class="flip-stat-value spiff">{{ formatCurrency(period.totalSpiff) }}</span>
                <span class="flip-stat-sub" v-if="period.spiffInvoiceCount > 0">
                  {{ period.spiffInvoiceCount }} invoices
                </span>
              </div>
            </div>
            
            <div class="flip-detail-grid">
              <div class="flip-detail-card">
                <span class="flip-detail-label">Regular</span>
                <span class="flip-detail-value">{{ formatCurrency(period.regularSales) }}</span>
              </div>
              <div class="flip-detail-card">
                <span class="flip-detail-label">New Layaway</span>
                <span class="flip-detail-value">{{ formatCurrency(period.layawayTotal - period.layawayPaid) }}</span>
              </div>
              <div class="flip-detail-card">
                <span class="flip-detail-label">Layaway Paid</span>
                <span class="flip-detail-value">{{ formatCurrency(period.layawayPaid) }}</span>
              </div>
              <div class="flip-detail-card">
                <span class="flip-detail-label">Total Refund</span>
                <span class="flip-detail-value text-danger">{{ formatCurrency(period.totalRefund) }}</span>
              </div>
              <div class="flip-detail-card">
                <span class="flip-detail-label">Sales Refund</span>
                <span class="flip-detail-value text-danger">{{ formatCurrency(period.salesRefund) }}</span>
              </div>
              <div class="flip-detail-card">
                <span class="flip-detail-label">Layaway Refund</span>
                <span class="flip-detail-value text-danger">{{ formatCurrency(period.layawayRefund) }}</span>
              </div>
            </div>
            
            <div class="flip-footer">
              <span class="flip-orders">📋 {{ period.orders }} transactions</span>
              <span class="flip-visits-footer">👥 {{ period.visits.total }} visits</span>
              <span class="flip-spiff-footer" v-if="period.spiffInvoiceCount > 0">
                💰 {{ formatCurrency(period.totalSpiff) }}
              </span>
              <div class="flip-actions">
                <span class="flip-hint" @click.stop="toggleFlip(period.key)">
                  <b-icon-arrow-repeat class="flip-hint-icon" />
                  Flip for charts
                </span>
              </div>
            </div>
          </div>
          
          <!-- BACK SIDE - Charts -->
          <div class="flip-card-back" @click="openPeriodModal(period.key)">
            <div class="flip-back-header">
              <span class="flip-back-label">{{ period.label }} - Charts</span>
              <span class="flip-hint back" @click.stop="toggleFlip(period.key)">
                <b-icon-arrow-repeat class="flip-hint-icon" />
                Flip back
              </span>
            </div>
            
            <div class="flip-charts-layout">
              <!-- Chart 1: Stats Distribution -->
              <div class="flip-chart-section">
                <div class="flip-chart-title">Sales Distribution</div>
                <div class="flip-chart-wrapper">
                  <canvas :id="'stats_pie_' + period.key" class="flip-chart"></canvas>
                </div>
                <div class="flip-chart-stats">
                  <div class="stats-label-item">
                    <span class="stats-dot regular-dot"></span>
                    <span class="stats-label-text">Regular</span>
                    <span class="stats-label-value">{{ formatCurrency(period.regularSales) }}</span>
                  </div>
                  <div class="stats-label-item">
                    <span class="stats-dot layaway-dot"></span>
                    <span class="stats-label-text">Layaway</span>
                    <span class="stats-label-value">{{ formatCurrency(period.layawayTotal) }}</span>
                  </div>
                  <div class="stats-label-item">
                    <span class="stats-dot refund-dot"></span>
                    <span class="stats-label-text">Refunds</span>
                    <span class="stats-label-value text-danger">{{ formatCurrency(period.totalRefund) }}</span>
                  </div>
                  <div class="stats-label-item" v-if="period.totalSpiff > 0">
                    <span class="stats-dot spiff-dot"></span>
                    <span class="stats-label-text">SPIFF</span>
                    <span class="stats-label-value spiff">{{ formatCurrency(period.totalSpiff) }}</span>
                  </div>
                </div>
              </div>
              
              <!-- Chart 2: Payment Methods -->
              <div class="flip-chart-section">
                <div class="flip-chart-title">Payment Methods</div>
                <div class="flip-chart-wrapper">
                  <canvas :id="'payment_pie_' + period.key" class="flip-chart"></canvas>
                </div>
                <div class="flip-chart-stats payment-stats">
                  <div v-if="period.paymentMethods && period.paymentMethods.length" 
                       v-for="pm in period.paymentMethods.slice(0, 4)" 
                       :key="pm.method"
                       class="stats-label-item">
                    <span class="stats-dot" :style="{ background: getColor(pm.method) }"></span>
                    <span class="stats-label-text">{{ pm.method }}</span>
                    <span class="stats-label-value">{{ formatCurrency(pm.amount) }}</span>
                  </div>
                  <div v-if="period.paymentMethods && period.paymentMethods.length > 4" class="stats-label-item more-methods">
                    <span class="stats-label-text">+{{ period.paymentMethods.length - 4 }} more</span>
                  </div>
                  <div v-if="!period.paymentMethods || !period.paymentMethods.length" class="stats-label-item">
                    <span class="stats-label-text text-muted">No payment data</span>
                  </div>
                </div>
              </div>
            </div>
            
            <div class="flip-back-footer">
              <span class="flip-orders">📋 {{ period.orders }} transactions</span>
              <span class="flip-visits-footer">👥 {{ period.visits.total }} visits</span>
              <span class="flip-payment-count">{{ period.paymentMethods ? period.paymentMethods.length : 0 }} payment methods</span>
              <span class="flip-spiff-footer" v-if="period.totalSpiff > 0">
                💰 SPIFF: {{ formatCurrency(period.totalSpiff) }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ===== MAIN CARDS ===== -->
    <div class="main-cards-grid" v-if="!isLoading">
      <div class="main-card" @click="openViewModal('daily')">
        <div class="main-card-icon daily">
          <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" stroke-width="2">
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
            <line x1="16" y1="2" x2="16" y2="6"/>
            <line x1="8" y1="2" x2="8" y2="6"/>
            <line x1="3" y1="10" x2="21" y2="10"/>
          </svg>
        </div>
        <div class="main-card-content">
          <h6>Daily Sales</h6>
          <p>View sales by day with week & month selection</p>
          <span class="main-card-cta">Explore →</span>
        </div>
      </div>
      
      <div class="main-card" @click="openViewModal('weekly')">
        <div class="main-card-icon weekly">
          <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" stroke-width="2">
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
            <line x1="16" y1="2" x2="16" y2="6"/>
            <line x1="8" y1="2" x2="8" y2="6"/>
            <line x1="3" y1="10" x2="21" y2="10"/>
            <path d="M8 14h.01M12 14h.01M16 14h.01M8 18h.01M12 18h.01M16 18h.01"/>
          </svg>
        </div>
        <div class="main-card-content">
          <h6>Weekly Sales</h6>
          <p>View sales by week with month & year selection</p>
          <span class="main-card-cta">Explore →</span>
        </div>
      </div>
      
      <div class="main-card" @click="openViewModal('monthly')">
        <div class="main-card-icon monthly">
          <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" stroke-width="2">
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
            <line x1="16" y1="2" x2="16" y2="6"/>
            <line x1="8" y1="2" x2="8" y2="6"/>
            <line x1="3" y1="10" x2="21" y2="10"/>
            <path d="M8 14h.01M12 14h.01M16 14h.01M8 18h.01M12 18h.01M16 18h.01"/>
            <path d="M10 14v4M14 14v4"/>
          </svg>
        </div>
        <div class="main-card-content">
          <h6>Monthly Sales</h6>
          <p>View sales by month with year selection</p>
          <span class="main-card-cta">Explore →</span>
        </div>
      </div>
    </div>

    <!-- ===== PERIOD DETAIL MODAL ===== -->
    <b-modal 
      v-model="showPeriodModal" 
      :title="periodModalTitle"
      size="xl"
      hide-footer
      body-class="period-modal-body"
      header-class="period-modal-header"
      @hidden="onPeriodModalClose"
    >
      <div v-if="isPeriodModalLoading" class="modal-loader">
        <div class="loader-3d small">
          <div class="cube">
            <div class="face front"></div>
            <div class="face back"></div>
            <div class="face right"></div>
            <div class="face left"></div>
            <div class="face top"></div>
            <div class="face bottom"></div>
          </div>
        </div>
        <p class="loader-text">Loading details...</p>
      </div>
      
      <div v-else-if="periodModalData">
        <div class="modal-summary-grid">
          <div class="modal-summary-item">
            <div class="summary-icon total">
              <b-icon-cash-stack />
            </div>
            <div>
              <span class="label">Total Sales</span>
              <span class="value">{{ formatCurrency(periodModalData.totalSales) }}</span>
            </div>
          </div>
          <div class="modal-summary-item">
            <div class="summary-icon net">
              <b-icon-wallet />
            </div>
            <div>
              <span class="label">Net Sales</span>
              <span class="value">{{ formatCurrency(periodModalData.netSales) }}</span>
            </div>
          </div>
          <div class="modal-summary-item">
            <div class="summary-icon regular">
              <b-icon-cart-plus />
            </div>
            <div>
              <span class="label">Regular Sales</span>
              <span class="value">{{ formatCurrency(periodModalData.regularSales) }}</span>
            </div>
          </div>
          <div class="modal-summary-item">
            <div class="summary-icon layaway">
              <b-icon-clock-history />
            </div>
            <div>
              <span class="label">Layaway Sales</span>
              <span class="value">{{ formatCurrency(periodModalData.layawayTotal) }}</span>
              <small>Paid: {{ formatCurrency(periodModalData.layawayPaid) }}</small>
            </div>
          </div>
          <div class="modal-summary-item">
            <div class="summary-icon refund">
              <b-icon-arrow-return-left />
            </div>
            <div>
              <span class="label">Total Refunds</span>
              <span class="value text-danger">{{ formatCurrency(periodModalData.totalRefund) }}</span>
              <small>Sales: {{ formatCurrency(periodModalData.salesRefund) }}</small>
            </div>
          </div>
          <div class="modal-summary-item">
            <div class="summary-icon orders">
              <b-icon-receipt />
            </div>
            <div>
              <span class="label">Orders</span>
              <span class="value">{{ periodModalData.totalOrders }}</span>
              <small>Avg: {{ formatCurrency(periodModalData.avgOrder) }}</small>
            </div>
          </div>
        </div>

        <!-- SPIFF Summary Section -->
        <div class="spiff-summary-section" v-if="periodModalData.spiff_summary && periodModalData.spiff_summary.total_spiff_value > 0">
          <div class="spiff-header">
            <span class="spiff-icon">💰</span>
            <span class="spiff-title">SPIFF Summary</span>
            <span class="spiff-badge" v-if="periodModalData.spiff_summary.spiff_invoice_count > 0">
              {{ periodModalData.spiff_summary.spiff_invoice_count }} Invoices
            </span>
          </div>
          <div class="spiff-grid">
            <div class="spiff-item">
              <span class="spiff-label">Total SPIFF Value</span>
              <span class="spiff-value">{{ formatCurrency(periodModalData.spiff_summary.total_spiff_value) }}</span>
            </div>
            <div class="spiff-item">
              <span class="spiff-label">SPIFF % of Sales</span>
              <span class="spiff-value">{{ periodModalData.spiff_summary.spiff_percentage_of_sales || 0 }}%</span>
            </div>
            <div class="spiff-item">
              <span class="spiff-label">Avg SPIFF per Invoice</span>
              <span class="spiff-value">{{ formatCurrency(periodModalData.spiff_summary.avg_spiff_per_invoice) }}</span>
            </div>
            <div class="spiff-item">
              <span class="spiff-label">Unique SPIFF Products</span>
              <span class="spiff-value">{{ periodModalData.spiff_summary.unique_spiff_products || 0 }}</span>
            </div>
            <div class="spiff-item">
              <span class="spiff-label">Salespeople with SPIFF</span>
              <span class="spiff-value">{{ periodModalData.spiff_summary.salespeople_with_spiffs || 0 }}</span>
            </div>
            <div class="spiff-item">
              <span class="spiff-label">Locations with SPIFF</span>
              <span class="spiff-value">{{ periodModalData.spiff_summary.locations_with_spiffs || 0 }}</span>
            </div>
          </div>
        </div>

        <!-- SPIFF Invoice Details -->
        <div class="spiff-details-section" v-if="periodModalData.spiff_summary && periodModalData.spiff_summary.spiff_invoice_details && periodModalData.spiff_summary.spiff_invoice_details.length">
          <h6 class="section-title">📋 Top SPIFF Invoices</h6>
          <div class="table-responsive-wrapper" style="max-height: 250px;">
            <table class="modal-table">
              <thead>
                <tr>
                  <th>Invoice</th>
                  <th>Salesperson</th>
                  <th>Location</th>
                  <th>Date</th>
                  <th class="text-right">Invoice Total</th>
                  <th class="text-right">SPIFF Base</th>
                  <th class="text-right">SPIFF Value</th>
                  <th class="text-right">Products</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="inv in periodModalData.spiff_summary.spiff_invoice_details" :key="inv.invoice_id">
                  <td><strong>{{ inv.invoice_id }}</strong></td>
                  <td>{{ inv.username }}</td>
                  <td>{{ getLocationName(inv.location_id) }}</td>
                  <td>{{ formatDate(inv.sale_date) }}</td>
                  <td class="text-right">{{ formatCurrency(inv.invoice_total) }}</td>
                  <td class="text-right">{{ formatCurrency(inv.spiff_base_amount) }}</td>
                  <td class="text-right spiff-value">{{ formatCurrency(inv.spiff_value) }}</td>
                  <td class="text-right">{{ inv.spiff_products_count || 0 }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Customer Visits Summary -->
        <div class="visits-summary-grid" v-if="periodModalData.visits">
          <div class="visits-summary-item">
            <div class="visits-icon total-visits">
              <b-icon-people />
            </div>
            <div>
              <span class="label">Total Visits</span>
              <span class="value">{{ periodModalData.visits.total }}</span>
            </div>
          </div>
          <div class="visits-summary-item">
            <div class="visits-icon unique">
              <b-icon-person />
            </div>
            <div>
              <span class="label">Unique Customers</span>
              <span class="value">{{ periodModalData.visits.unique_customers }}</span>
            </div>
          </div>
          <div class="visits-summary-item">
            <div class="visits-icon completed">
              <b-icon-check-circle />
            </div>
            <div>
              <span class="label">Completed</span>
              <span class="value">{{ periodModalData.visits.completed }}</span>
            </div>
          </div>
          <div class="visits-summary-item">
            <div class="visits-icon active">
              <b-icon-clock />
            </div>
            <div>
              <span class="label">Active Visits</span>
              <span class="value">{{ periodModalData.visits.active }}</span>
            </div>
          </div>
        </div>

        <b-tabs pills card class="modal-tabs">
          <b-tab title="💳 Payment Methods" active>
            <div class="payment-methods-grid">
              <div 
                v-for="pm in periodModalData.paymentMethods" 
                :key="pm.method"
                class="payment-method-card"
              >
                <div class="payment-method-header">
                  <span class="payment-method-name">
                    <span class="method-dot" :style="{ background: getColor(pm.method) }"></span>
                    {{ pm.method }}
                  </span>
                  <span class="payment-method-amount">{{ formatCurrency(pm.amount) }}</span>
                </div>
                <div class="payment-bar">
                  <div class="payment-bar-fill" :style="{ width: pm.percentage + '%', background: getColor(pm.method) }"></div>
                </div>
                <div class="payment-method-footer">
                  <span>{{ pm.count }} transactions</span>
                  <span>{{ pm.percentage.toFixed(1) }}%</span>
                </div>
                <div v-if="pm.subMethods && pm.subMethods.length" class="sub-methods">
                  <div v-for="sub in pm.subMethods" :key="sub.name" class="sub-method">
                    <span class="sub-method-name">↳ {{ sub.name }}</span>
                    <span class="sub-method-amount">{{ formatCurrency(sub.amount) }}</span>
                  </div>
                </div>
              </div>
            </div>
          </b-tab>
          
          <b-tab title="👤 Salespeople">
            <div class="table-responsive-wrapper">
              <table class="modal-table">
                <thead>
                  <tr>
                    <th>Salesperson</th>
                    <th class="text-right">Total</th>
                    <th class="text-right">Net</th>
                    <th class="text-right">Regular</th>
                    <th class="text-right">Layaway</th>
                    <th class="text-right">Layaway Paid</th>
                    <th class="text-right">Refunds</th>
                    <th class="text-right">Orders</th>
                    <th class="text-right">SPIFF</th>
                    <th class="text-center">Action</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="sp in periodModalData.salespeople" :key="sp.name">
                    <td><strong>{{ sp.name }}</strong></td>
                    <td class="text-right">{{ formatCurrency(sp.total_sales || 0) }}</td>
                    <td class="text-right">{{ formatCurrency(sp.net_sales || 0) }}</td>
                    <td class="text-right">{{ formatCurrency(sp.regular_sales || 0) }}</td>
                    <td class="text-right">{{ formatCurrency(sp.layaway_total || 0) }}</td>
                    <td class="text-right">{{ formatCurrency(sp.layaway_paid || 0) }}</td>
                    <td class="text-right text-danger">{{ formatCurrency(sp.total_refund || 0) }}</td>
                    <td class="text-right">{{ sp.orders || 0 }}</td>
                    <td class="text-right spiff-value">{{ formatCurrency(sp.spiff || 0) }}</td>
                    <td class="text-center">
                      <b-button size="sm" variant="outline-primary" @click="openSalespersonDetail(sp)">
                        <b-icon-search size="sm" />
                      </b-button>
                    </td>
                  </tr>
                </tbody>
                <tfoot>
                  <tr class="table-total">
                    <td><strong>TOTAL</strong></td>
                    <td class="text-right"><strong>{{ formatCurrency(periodModalData.totalSales || 0) }}</strong></td>
                    <td class="text-right"><strong>{{ formatCurrency(periodModalData.netSales || 0) }}</strong></td>
                    <td class="text-right"><strong>{{ formatCurrency(periodModalData.regularSales || 0) }}</strong></td>
                    <td class="text-right"><strong>{{ formatCurrency(periodModalData.layawayTotal || 0) }}</strong></td>
                    <td class="text-right"><strong>{{ formatCurrency(periodModalData.layawayPaid || 0) }}</strong></td>
                    <td class="text-right"><strong>{{ formatCurrency(periodModalData.totalRefund || 0) }}</strong></td>
                    <td class="text-right"><strong>{{ periodModalData.totalOrders || 0 }}</strong></td>
                    <td class="text-right spiff-value"><strong>{{ formatCurrency(periodModalData.spiff_summary ? periodModalData.spiff_summary.total_spiff_value : 0) }}</strong></td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </b-tab>
          
          <b-tab title="📍 Locations">
            <div class="table-responsive-wrapper">
              <table class="modal-table">
                <thead>
                  <tr>
                    <th>Location</th>
                    <th class="text-right">Total</th>
                    <th class="text-right">Net</th>
                    <th class="text-right">Regular</th>
                    <th class="text-right">Layaway</th>
                    <th class="text-right">Layaway Paid</th>
                    <th class="text-right">Refunds</th>
                    <th class="text-right">Orders</th>
                    <th class="text-right">Visits</th>
                    <th class="text-right">SPIFF</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="loc in periodModalData.locations" :key="loc.id">
                    <td>{{ loc.name }}</td>
                    <td class="text-right">{{ formatCurrency(loc.total_sales || 0) }}</td>
                    <td class="text-right">{{ formatCurrency(loc.net_sales || 0) }}</td>
                    <td class="text-right">{{ formatCurrency(loc.regular_sales || 0) }}</td>
                    <td class="text-right">{{ formatCurrency(loc.layaway_total || 0) }}</td>
                    <td class="text-right">{{ formatCurrency(loc.layaway_paid || 0) }}</td>
                    <td class="text-right text-danger">{{ formatCurrency(loc.total_refund || 0) }}</td>
                    <td class="text-right">{{ loc.orders || 0 }}</td>
                    <td class="text-right">{{ loc.visits || 0 }}</td>
                    <td class="text-right spiff-value">{{ formatCurrency(loc.spiff || 0) }}</td>
                  </tr>
                </tbody>
                <tfoot>
                  <tr class="table-total">
                    <td><strong>TOTAL</strong></td>
                    <td class="text-right"><strong>{{ formatCurrency(periodModalData.totalSales || 0) }}</strong></td>
                    <td class="text-right"><strong>{{ formatCurrency(periodModalData.netSales || 0) }}</strong></td>
                    <td class="text-right"><strong>{{ formatCurrency(periodModalData.regularSales || 0) }}</strong></td>
                    <td class="text-right"><strong>{{ formatCurrency(periodModalData.layawayTotal || 0) }}</strong></td>
                    <td class="text-right"><strong>{{ formatCurrency(periodModalData.layawayPaid || 0) }}</strong></td>
                    <td class="text-right"><strong>{{ formatCurrency(periodModalData.totalRefund || 0) }}</strong></td>
                    <td class="text-right"><strong>{{ periodModalData.totalOrders || 0 }}</strong></td>
                    <td class="text-right"><strong>{{ periodModalData.visits ? periodModalData.visits.total : 0 }}</strong></td>
                    <td class="text-right spiff-value"><strong>{{ formatCurrency(periodModalData.spiff_summary ? periodModalData.spiff_summary.total_spiff_value : 0) }}</strong></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </b-tab>
          
          <b-tab title="👥 Customer Visits">
            <div class="visits-detail-grid">
              <div class="visits-stats-grid">
                <div class="visits-stat-card">
                  <span class="visits-stat-label">Total Visits</span>
                  <span class="visits-stat-value">{{ periodModalData.visits ? periodModalData.visits.total : 0 }}</span>
                </div>
                <div class="visits-stat-card">
                  <span class="visits-stat-label">Unique Customers</span>
                  <span class="visits-stat-value">{{ periodModalData.visits ? periodModalData.visits.unique_customers : 0 }}</span>
                </div>
                <div class="visits-stat-card">
                  <span class="visits-stat-label">Completed</span>
                  <span class="visits-stat-value">{{ periodModalData.visits ? periodModalData.visits.completed : 0 }}</span>
                </div>
                <div class="visits-stat-card">
                  <span class="visits-stat-label">Active</span>
                  <span class="visits-stat-value">{{ periodModalData.visits ? periodModalData.visits.active : 0 }}</span>
                </div>
              </div>

              <div class="visits-action-breakdown">
                <h6 class="section-title">📊 Action Type Breakdown</h6>
                <div class="action-type-grid">
                  <div 
                    v-for="(count, type) in (periodModalData.visits ? periodModalData.visits.action_types : {})" 
                    :key="type"
                    class="action-type-card"
                  >
                    <span class="action-type-name">{{ type.replace('_', ' ').toUpperCase() }}</span>
                    <span class="action-type-count">{{ count }}</span>
                    <div class="action-type-bar">
                      <div 
                        class="action-type-bar-fill" 
                        :style="{ width: ((count / (periodModalData.visits ? periodModalData.visits.total : 1)) * 100) + '%' }"
                      ></div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="table-responsive-wrapper" style="max-height: 350px;">
                <table class="modal-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Phone</th>
                      <th>Check In</th>
                      <th>Check Out</th>
                      <th>Action Type</th>
                      <th>Created By</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="visit in paginatedVisits" :key="visit.id">
                      <td><strong>{{ visit.name || 'N/A' }}</strong></td>
                      <td>{{ visit.email || 'N/A' }}</td>
                      <td>{{ visit.phone || 'N/A' }}</td>
                      <td>{{ formatDate(visit.check_in_time) }}</td>
                      <td>{{ visit.check_out_time ? formatDate(visit.check_out_time) : '-' }}</td>
                      <td>
                        <b-badge :variant="getActionTypeVariant(visit.action_type)" pill>
                          {{ visit.action_type || 'unknown' }}
                        </b-badge>
                      </td>
                      <td>{{ visit.created_by || 'N/A' }}</td>
                    </tr>
                    <tr v-if="!paginatedVisits.length">
                      <td colspan="7" class="text-center text-muted">No visit data available</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div class="modal-pagination">
                <span class="text-muted small">
                  {{ (visitPage - 1) * visitPageSize + 1 }} - {{ Math.min(visitPage * visitPageSize, periodModalData.visits ? periodModalData.visits.list ? periodModalData.visits.list.length : 0 : 0) }} of {{ periodModalData.visits ? periodModalData.visits.list ? periodModalData.visits.list.length : 0 : 0 }}
                </span>
                <b-pagination
                  v-model="visitPage"
                  :total-rows="periodModalData.visits ? periodModalData.visits.list ? periodModalData.visits.list.length : 0 : 0"
                  :per-page="visitPageSize"
                  size="sm"
                  class="mb-0"
                />
                <b-form-select v-model="visitPageSize" size="sm" style="width: 70px;">
                  <option :value="10">10</option>
                  <option :value="25">25</option>
                  <option :value="50">50</option>
                </b-form-select>
              </div>
            </div>
          </b-tab>
          
          <b-tab title="💰 SPIFF Details">
            <div v-if="periodModalData.spiff_summary && periodModalData.spiff_summary.total_spiff_value > 0">
              <div class="spiff-summary-cards">
                <div class="spiff-card">
                  <span class="spiff-card-label">Total SPIFF</span>
                  <span class="spiff-card-value">{{ formatCurrency(periodModalData.spiff_summary.total_spiff_value) }}</span>
                </div>
                <div class="spiff-card">
                  <span class="spiff-card-label">SPIFF Invoices</span>
                  <span class="spiff-card-value">{{ periodModalData.spiff_summary.spiff_invoice_count }}</span>
                </div>
                <div class="spiff-card">
                  <span class="spiff-card-label">% of Sales</span>
                  <span class="spiff-card-value">{{ periodModalData.spiff_summary.spiff_percentage_of_sales || 0 }}%</span>
                </div>
                <div class="spiff-card">
                  <span class="spiff-card-label">Avg per Invoice</span>
                  <span class="spiff-card-value">{{ formatCurrency(periodModalData.spiff_summary.avg_spiff_per_invoice) }}</span>
                </div>
                <div class="spiff-card">
                  <span class="spiff-card-label">Unique Products</span>
                  <span class="spiff-card-value">{{ periodModalData.spiff_summary.unique_spiff_products || 0 }}</span>
                </div>
                <div class="spiff-card">
                  <span class="spiff-card-label">Salespeople</span>
                  <span class="spiff-card-value">{{ periodModalData.spiff_summary.salespeople_with_spiffs || 0 }}</span>
                </div>
              </div>

              <div v-if="periodModalData.spiff_summary.spiff_invoice_details && periodModalData.spiff_summary.spiff_invoice_details.length" class="spiff-invoice-table">
                <h6 class="section-title">📋 SPIFF Invoice Details</h6>
                <div class="table-responsive-wrapper" style="max-height: 300px;">
                  <table class="modal-table">
                    <thead>
                      <tr>
                        <th>Invoice</th>
                        <th>Salesperson</th>
                        <th>Location</th>
                        <th>Date</th>
                        <th class="text-right">Invoice Total</th>
                        <th class="text-right">SPIFF Base</th>
                        <th class="text-right">SPIFF Value</th>
                        <th class="text-right">Products</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="inv in periodModalData.spiff_summary.spiff_invoice_details" :key="inv.invoice_id">
                        <td><strong>{{ inv.invoice_id }}</strong></td>
                        <td>{{ inv.username }}</td>
                        <td>{{ getLocationName(inv.location_id) }}</td>
                        <td>{{ formatDate(inv.sale_date) }}</td>
                        <td class="text-right">{{ formatCurrency(inv.invoice_total) }}</td>
                        <td class="text-right">{{ formatCurrency(inv.spiff_base_amount) }}</td>
                        <td class="text-right spiff-value">{{ formatCurrency(inv.spiff_value) }}</td>
                        <td class="text-right">{{ inv.spiff_products_count || 0 }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            <div v-else class="text-center text-muted py-4">
              <p>No SPIFF data available for this period</p>
            </div>
          </b-tab>
          
          <b-tab title="📋 Transactions">
            <div class="table-responsive-wrapper" style="max-height: 400px;">
              <table class="modal-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Invoice</th>
                    <th>Location</th>
                    <th>Salesperson</th>
                    <th>Payment</th>
                    <th class="text-right">Amount</th>
                    <th class="text-right">SPIFF</th>
                    <th class="text-center">Type</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="tx in paginatedTransactions" :key="tx.invoice_id + tx.ts">
                    <td>{{ formatDate(tx.ts) }}</td>
                    <td>{{ tx.invoice_id }}</td>
                    <td>{{ getLocationName(tx.location_id) }}</td>
                    <td>{{ tx.username }}</td>
                    <td>{{ getPaymentMethodsString(tx.payment_methods) }}</td>
                    <td class="text-right" :class="{ 'text-danger': tx.total_dollar_amount < 0 }">
                      {{ formatCurrency(tx.total_dollar_amount) }}
                    </td>
                    <td class="text-right spiff-value" v-if="tx.spiff_commission">
                      {{ formatCurrency(tx.spiff_commission) }}
                    </td>
                    <td class="text-right text-muted" v-else>-</td>
                    <td class="text-center">
                      <b-badge :variant="tx.is_refund_entry ? 'danger' : 'success'" pill>
                        {{ tx.is_refund_entry ? 'Refund' : 'Sale' }}
                      </b-badge>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div class="modal-pagination">
              <span class="text-muted small">
                {{ (transactionPage - 1) * transactionPageSize + 1 }} - {{ Math.min(transactionPage * transactionPageSize, periodModalData.transactions.length) }} of {{ periodModalData.transactions.length }}
              </span>
              <b-pagination
                v-model="transactionPage"
                :total-rows="periodModalData.transactions.length"
                :per-page="transactionPageSize"
                size="sm"
                class="mb-0"
              />
              <b-form-select v-model="transactionPageSize" size="sm" style="width: 70px;">
                <option :value="10">10</option>
                <option :value="25">25</option>
                <option :value="50">50</option>
              </b-form-select>
            </div>
          </b-tab>
        </b-tabs>
      </div>
    </b-modal>

    <!-- ===== SALESPERSON DETAIL MODAL ===== -->
    <b-modal 
      v-model="showSalespersonDetail" 
      :title="salespersonDetailTitle"
      size="lg"
      hide-footer
      body-class="detail-modal-body"
      header-class="detail-modal-header"
    >
      <div v-if="isSalespersonDetailLoading" class="modal-loader">
        <div class="loader-3d small">
          <div class="cube">
            <div class="face front"></div>
            <div class="face back"></div>
            <div class="face right"></div>
            <div class="face left"></div>
            <div class="face top"></div>
            <div class="face bottom"></div>
          </div>
        </div>
        <p class="loader-text">Loading details...</p>
      </div>
      
      <div v-else-if="salespersonDetailData">
        <div class="detail-summary-grid">
          <div class="detail-summary-item">
            <span class="label">Total Sales</span>
            <span class="value">{{ formatCurrency(salespersonDetailData.totalSales) }}</span>
          </div>
          <div class="detail-summary-item">
            <span class="label">Net Sales</span>
            <span class="value">{{ formatCurrency(salespersonDetailData.netSales) }}</span>
          </div>
          <div class="detail-summary-item">
            <span class="label">Orders</span>
            <span class="value">{{ salespersonDetailData.orders }}</span>
          </div>
          <div class="detail-summary-item">
            <span class="label">Avg Order</span>
            <span class="value">{{ formatCurrency(salespersonDetailData.avgOrder) }}</span>
          </div>
          <div class="detail-summary-item spiff-highlight">
            <span class="label">💰 SPIFF</span>
            <span class="value spiff-value">{{ formatCurrency(salespersonDetailData.totalSpiff || 0) }}</span>
          </div>
        </div>

        <h6 class="section-title">💳 Payment Methods</h6>
        <div class="payment-methods-grid small">
          <div 
            v-for="pm in salespersonDetailData.paymentMethods" 
            :key="pm.method"
            class="payment-method-card small"
          >
            <div class="payment-method-header">
              <span class="payment-method-name">
                <span class="method-dot" :style="{ background: getColor(pm.method) }"></span>
                {{ pm.method }}
              </span>
              <span class="payment-method-amount">{{ formatCurrency(pm.amount) }}</span>
            </div>
            <div class="payment-bar">
              <div class="payment-bar-fill" :style="{ width: pm.percentage + '%', background: getColor(pm.method) }"></div>
            </div>
          </div>
        </div>

        <h6 class="section-title">📋 Transactions</h6>
        <div class="table-responsive-wrapper" style="max-height: 300px;">
          <table class="modal-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Invoice</th>
                <th>Location</th>
                <th>Payment</th>
                <th class="text-right">Amount</th>
                <th class="text-right">SPIFF</th>
                <th class="text-center">Type</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="tx in salespersonDetailData.transactions" :key="tx.invoice_id">
                <td>{{ formatDate(tx.ts) }}</td>
                <td>{{ tx.invoice_id }}</td>
                <td>{{ getLocationName(tx.location_id) }}</td>
                <td>{{ getPaymentMethodsString(tx.payment_methods) }}</td>
                <td class="text-right" :class="{ 'text-danger': tx.total_dollar_amount < 0 }">
                  {{ formatCurrency(tx.total_dollar_amount) }}
                </td>
                <td class="text-right spiff-value" v-if="tx.spiff_commission">
                  {{ formatCurrency(tx.spiff_commission) }}
                </td>
                <td class="text-right text-muted" v-else>-</td>
                <td class="text-center">
                  <b-badge :variant="tx.is_refund_entry ? 'danger' : 'success'" pill>
                    {{ tx.is_refund_entry ? 'Refund' : 'Sale' }}
                  </b-badge>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </b-modal>

    <!-- ===== MAIN VIEW MODAL WITH TREND CHART ===== -->
    <b-modal 
      v-model="showViewModal" 
      :title="viewModalTitle"
      size="xl"
      hide-footer
      body-class="view-modal-body"
      header-class="view-modal-header"
      @shown="onViewModalShown"
    >
      <div v-if="isViewModalLoading" class="modal-loader">
        <div class="loader-3d small">
          <div class="cube">
            <div class="face front"></div>
            <div class="face back"></div>
            <div class="face right"></div>
            <div class="face left"></div>
            <div class="face top"></div>
            <div class="face bottom"></div>
          </div>
        </div>
        <p class="loader-text">Loading {{ viewType }} data...</p>
      </div>
      
      <div v-else-if="viewModalData">
        <!-- Filters -->
        <div class="view-filters">
          <div class="filters-row">
            <div class="filter-group" v-if="viewType === 'daily' || viewType === 'weekly'">
              <label>Month</label>
              <b-form-select v-model="viewFilters.month" :options="monthOptions" size="sm" @change="fetchViewData" />
            </div>
            <div class="filter-group" v-if="viewType === 'daily' || viewType === 'weekly'">
              <label>Year</label>
              <b-form-select v-model="viewFilters.year" :options="yearOptions" size="sm" @change="fetchViewData" />
            </div>
            <div class="filter-group" v-if="viewType === 'daily'">
              <label>Week</label>
              <b-form-select v-model="viewFilters.week" :options="weekOptions" size="sm" @change="fetchViewData" />
            </div>
            <div class="filter-group">
              <label>Location</label>
              <b-form-select v-model="viewFilters.location" :options="viewLocationOptions" size="sm" @change="fetchViewData">
              </b-form-select>
            </div>
            <div class="filter-group">
              <label>Salesperson</label>
              <b-form-select v-model="viewFilters.salesperson" :options="viewSalespersonOptions" size="sm" @change="fetchViewData">
              </b-form-select>
            </div>
            <div class="filter-group filter-actions">
              <b-button variant="primary" size="sm" @click="fetchViewData" :disabled="isViewModalLoading">
                <b-icon-search class="mr-1" size="sm" /> Apply
              </b-button>
            </div>
          </div>
        </div>

        <!-- Summary Cards -->
        <div class="view-summary-grid">
          <div class="view-summary-item">
            <span class="label">Total Sales</span>
            <span class="value">{{ formatCurrency(viewModalData.totalSales) }}</span>
          </div>
          <div class="view-summary-item">
            <span class="label">Net Sales</span>
            <span class="value">{{ formatCurrency(viewModalData.netSales) }}</span>
          </div>
          <div class="view-summary-item">
            <span class="label">Regular</span>
            <span class="value">{{ formatCurrency(viewModalData.regularSales) }}</span>
          </div>
          <div class="view-summary-item">
            <span class="label">Layaway</span>
            <span class="value">{{ formatCurrency(viewModalData.layawayTotal) }}</span>
          </div>
          <div class="view-summary-item">
            <span class="label">Refunds</span>
            <span class="value text-danger">{{ formatCurrency(viewModalData.totalRefund) }}</span>
          </div>
          <div class="view-summary-item">
            <span class="label">Orders</span>
            <span class="value">{{ viewModalData.totalOrders }}</span>
          </div>
        </div>

        <!-- SPIFF Summary Mini -->
        <div class="spiff-summary-mini" v-if="viewModalData.spiff_summary && viewModalData.spiff_summary.total_spiff_value > 0">
          <div class="spiff-mini-grid">
            <div class="spiff-mini-item">
              <span class="spiff-mini-label">💰 Total SPIFF</span>
              <span class="spiff-mini-value">{{ formatCurrency(viewModalData.spiff_summary.total_spiff_value) }}</span>
            </div>
            <div class="spiff-mini-item">
              <span class="spiff-mini-label">SPIFF Invoices</span>
              <span class="spiff-mini-value">{{ viewModalData.spiff_summary.spiff_invoice_count }}</span>
            </div>
            <div class="spiff-mini-item">
              <span class="spiff-mini-label">% of Sales</span>
              <span class="spiff-mini-value">{{ viewModalData.spiff_summary.spiff_percentage_of_sales || 0 }}%</span>
            </div>
            <div class="spiff-mini-item">
              <span class="spiff-mini-label">Avg SPIFF</span>
              <span class="spiff-mini-value">{{ formatCurrency(viewModalData.spiff_summary.avg_spiff_per_invoice) }}</span>
            </div>
          </div>
        </div>

        <!-- ===== TREND CHART ===== -->
        <div class="trend-chart-container">
          <div class="trend-chart-header">
            <div class="trend-chart-title">
              <b-icon-bar-chart-line class="mr-2" />
              <span>{{ viewType.charAt(0).toUpperCase() + viewType.slice(1) }} Sales Trend</span>
            </div>
            <div class="trend-chart-legend">
              <span class="legend-item">
                <span class="legend-dot total-dot"></span> Total Sales
              </span>
              <span class="legend-item">
                <span class="legend-dot net-dot"></span> Net Sales
              </span>
              <span class="legend-item">
                <span class="legend-dot refund-dot"></span> Refunds
              </span>
              <span class="legend-item" v-if="viewModalData.spiff_summary && viewModalData.spiff_summary.total_spiff_value > 0">
                <span class="legend-dot spiff-dot"></span> SPIFF
              </span>
            </div>
          </div>
          <div class="trend-chart-wrapper">
            <canvas id="trendChart"></canvas>
          </div>
        </div>

        <!-- Data Table -->
        <div class="table-responsive-wrapper" style="max-height: 400px;">
          <table class="modal-table">
            <thead>
              <tr>
                <th>{{ viewType === 'daily' ? 'Day' : viewType === 'weekly' ? 'Week' : 'Month' }}</th>
                <th class="text-right">Total</th>
                <th class="text-right">Net</th>
                <th class="text-right">Regular</th>
                <th class="text-right">Layaway</th>
                <th class="text-right">Layaway Paid</th>
                <th class="text-right">Refunds</th>
                <th class="text-right">Orders</th>
                <th class="text-right">Visits</th>
                <th class="text-right">SPIFF</th>
                <th class="text-right">Avg Order</th>
                <th class="text-center">Action</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, idx) in viewModalData.data" :key="idx">
                <td><strong>{{ item.label }}</strong></td>
                <td class="text-right">{{ formatCurrency(item.totalSales) }}</td>
                <td class="text-right">{{ formatCurrency(item.netSales) }}</td>
                <td class="text-right">{{ formatCurrency(item.regularSales) }}</td>
                <td class="text-right">{{ formatCurrency(item.layawayTotal) }}</td>
                <td class="text-right">{{ formatCurrency(item.layawayPaid) }}</td>
                <td class="text-right text-danger">{{ formatCurrency(item.totalRefund) }}</td>
                <td class="text-right">{{ item.orders }}</td>
                <td class="text-right">{{ item.visits || 0 }}</td>
                <td class="text-right spiff-value">{{ formatCurrency(item.totalSpiff || 0) }}</td>
                <td class="text-right">{{ formatCurrency(item.avgOrder) }}</td>
                <td class="text-center">
                  <b-button size="sm" variant="outline-primary" @click="openItemDetail(item, viewType)">
                    <b-icon-search size="sm" />
                  </b-button>
                </td>
              </tr>
            </tbody>
            <tfoot>
              <tr class="table-total">
                <td><strong>TOTAL</strong></td>
                <td class="text-right"><strong>{{ formatCurrency(viewModalData.totalSales) }}</strong></td>
                <td class="text-right"><strong>{{ formatCurrency(viewModalData.netSales) }}</strong></td>
                <td class="text-right"><strong>{{ formatCurrency(viewModalData.regularSales) }}</strong></td>
                <td class="text-right"><strong>{{ formatCurrency(viewModalData.layawayTotal) }}</strong></td>
                <td class="text-right"><strong>{{ formatCurrency(viewModalData.layawayPaid) }}</strong></td>
                <td class="text-right"><strong>{{ formatCurrency(viewModalData.totalRefund) }}</strong></td>
                <td class="text-right"><strong>{{ viewModalData.totalOrders }}</strong></td>
                <td class="text-right"><strong>{{ viewModalData.totalVisits || 0 }}</strong></td>
                <td class="text-right spiff-value"><strong>{{ formatCurrency(viewModalData.totalSpiff || 0) }}</strong></td>
                <td class="text-right"><strong>{{ formatCurrency(viewModalData.avgOrder) }}</strong></td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </b-modal>

    <!-- ===== ITEM DETAIL MODAL ===== -->
    <b-modal 
      v-model="showItemDetail" 
      :title="itemDetailTitle"
      size="xl"
      hide-footer
      body-class="detail-modal-body"
      header-class="detail-modal-header"
    >
      <div v-if="isItemDetailLoading" class="modal-loader">
        <div class="loader-3d small">
          <div class="cube">
            <div class="face front"></div>
            <div class="face back"></div>
            <div class="face right"></div>
            <div class="face left"></div>
            <div class="face top"></div>
            <div class="face bottom"></div>
          </div>
        </div>
        <p class="loader-text">Loading details...</p>
      </div>
      
      <div v-else-if="itemDetailData">
        <!-- Summary Cards - All fields -->
        <div class="detail-summary-grid-expanded">
          <div class="detail-summary-item">
            <span class="label">Total Sales</span>
            <span class="value">{{ formatCurrency(itemDetailData.totalSales) }}</span>
          </div>
          <div class="detail-summary-item">
            <span class="label">Net Sales</span>
            <span class="value">{{ formatCurrency(itemDetailData.netSales) }}</span>
          </div>
          <div class="detail-summary-item">
            <span class="label">Regular</span>
            <span class="value">{{ formatCurrency(itemDetailData.regularSales) }}</span>
          </div>
          <div class="detail-summary-item">
            <span class="label">Layaway Total</span>
            <span class="value">{{ formatCurrency(itemDetailData.layawayTotal) }}</span>
          </div>
          <div class="detail-summary-item">
            <span class="label">New Layaway</span>
            <span class="value">{{ formatCurrency(itemDetailData.newLayaway) }}</span>
          </div>
          <div class="detail-summary-item">
            <span class="label">Layaway Paid</span>
            <span class="value">{{ formatCurrency(itemDetailData.layawayPaid) }}</span>
          </div>
          <div class="detail-summary-item">
            <span class="label">Total Refund</span>
            <span class="value text-danger">{{ formatCurrency(itemDetailData.totalRefund) }}</span>
          </div>
          <div class="detail-summary-item">
            <span class="label">Sales Refund</span>
            <span class="value text-danger">{{ formatCurrency(itemDetailData.salesRefund) }}</span>
          </div>
          <div class="detail-summary-item">
            <span class="label">Layaway Refund</span>
            <span class="value text-danger">{{ formatCurrency(itemDetailData.layawayRefund) }}</span>
          </div>
          <div class="detail-summary-item">
            <span class="label">Orders</span>
            <span class="value">{{ itemDetailData.orders }}</span>
          </div>
          <div class="detail-summary-item">
            <span class="label">Visits</span>
            <span class="value">{{ itemDetailData.visits || 0 }}</span>
          </div>
          <div class="detail-summary-item spiff-highlight">
            <span class="label">💰 SPIFF</span>
            <span class="value spiff-value">{{ formatCurrency(itemDetailData.totalSpiff || 0) }}</span>
          </div>
        </div>

        <!-- Tabs -->
        <b-tabs pills card class="modal-tabs item-detail-tabs">
          <b-tab title="💳 Payment Methods" active>
            <div class="payment-methods-grid small">
              <div 
                v-for="pm in itemDetailData.paymentMethods" 
                :key="pm.method"
                class="payment-method-card small"
              >
                <div class="payment-method-header">
                  <span class="payment-method-name">
                    <span class="method-dot" :style="{ background: getColor(pm.method) }"></span>
                    {{ pm.method }}
                  </span>
                  <span class="payment-method-amount">{{ formatCurrency(pm.amount) }}</span>
                </div>
                <div class="payment-bar">
                  <div class="payment-bar-fill" :style="{ width: pm.percentage + '%', background: getColor(pm.method) }"></div>
                </div>
                <div class="payment-method-footer">
                  <span>{{ pm.count || 0 }} transactions</span>
                  <span>{{ pm.percentage ? pm.percentage.toFixed(1) : 0 }}%</span>
                </div>
              </div>
              <div v-if="!itemDetailData.paymentMethods || !itemDetailData.paymentMethods.length" class="text-muted text-center py-3">
                No payment data available
              </div>
            </div>
          </b-tab>
          
          <b-tab title="📍 Locations">
            <div class="table-responsive-wrapper" style="max-height: 350px;">
              <table class="modal-table">
                <thead>
                  <tr>
                    <th>Location</th>
                    <th class="text-right">Total</th>
                    <th class="text-right">Net</th>
                    <th class="text-right">Regular</th>
                    <th class="text-right">Layaway</th>
                    <th class="text-right">Layaway Paid</th>
                    <th class="text-right">Refunds</th>
                    <th class="text-right">Orders</th>
                    <th class="text-right">Visits</th>
                    <th class="text-right">SPIFF</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="loc in itemDetailData.locations" :key="loc.id">
                    <td><strong>{{ loc.name }}</strong></td>
                    <td class="text-right">{{ formatCurrency(loc.total_sales || 0) }}</td>
                    <td class="text-right">{{ formatCurrency(loc.net_sales || 0) }}</td>
                    <td class="text-right">{{ formatCurrency(loc.regular_sales || 0) }}</td>
                    <td class="text-right">{{ formatCurrency(loc.layaway_total || 0) }}</td>
                    <td class="text-right">{{ formatCurrency(loc.layaway_paid || 0) }}</td>
                    <td class="text-right text-danger">{{ formatCurrency(loc.total_refund || 0) }}</td>
                    <td class="text-right">{{ loc.orders || 0 }}</td>
                    <td class="text-right">{{ loc.visits || 0 }}</td>
                    <td class="text-right spiff-value">{{ formatCurrency(loc.spiff || 0) }}</td>
                  </tr>
                  <tr v-if="!itemDetailData.locations || !itemDetailData.locations.length">
                    <td colspan="10" class="text-center text-muted">No location data available</td>
                  </tr>
                </tbody>
                <tfoot v-if="itemDetailData.locations && itemDetailData.locations.length">
                  <tr class="table-total">
                    <td><strong>TOTAL</strong></td>
                    <td class="text-right"><strong>{{ formatCurrency(itemDetailData.totalSales) }}</strong></td>
                    <td class="text-right"><strong>{{ formatCurrency(itemDetailData.netSales) }}</strong></td>
                    <td class="text-right"><strong>{{ formatCurrency(itemDetailData.regularSales) }}</strong></td>
                    <td class="text-right"><strong>{{ formatCurrency(itemDetailData.layawayTotal) }}</strong></td>
                    <td class="text-right"><strong>{{ formatCurrency(itemDetailData.layawayPaid) }}</strong></td>
                    <td class="text-right"><strong>{{ formatCurrency(itemDetailData.totalRefund) }}</strong></td>
                    <td class="text-right"><strong>{{ itemDetailData.orders }}</strong></td>
                    <td class="text-right"><strong>{{ itemDetailData.visits || 0 }}</strong></td>
                    <td class="text-right spiff-value"><strong>{{ formatCurrency(itemDetailData.totalSpiff || 0) }}</strong></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </b-tab>

          <b-tab title="👤 Salespeople">
            <div class="table-responsive-wrapper" style="max-height: 350px;">
              <table class="modal-table">
                <thead>
                  <tr>
                    <th>Salesperson</th>
                    <th class="text-right">Total</th>
                    <th class="text-right">Net</th>
                    <th class="text-right">Regular</th>
                    <th class="text-right">Layaway</th>
                    <th class="text-right">Layaway Paid</th>
                    <th class="text-right">Refunds</th>
                    <th class="text-right">Orders</th>
                    <th class="text-right">SPIFF</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="sp in itemDetailData.salespeople" :key="sp.name">
                    <td><strong>{{ sp.name }}</strong></td>
                    <td class="text-right">{{ formatCurrency(sp.total_sales || 0) }}</td>
                    <td class="text-right">{{ formatCurrency(sp.net_sales || 0) }}</td>
                    <td class="text-right">{{ formatCurrency(sp.regular_sales || 0) }}</td>
                    <td class="text-right">{{ formatCurrency(sp.layaway_total || 0) }}</td>
                    <td class="text-right">{{ formatCurrency(sp.layaway_paid || 0) }}</td>
                    <td class="text-right text-danger">{{ formatCurrency(sp.total_refund || 0) }}</td>
                    <td class="text-right">{{ sp.orders || 0 }}</td>
                    <td class="text-right spiff-value">{{ formatCurrency(sp.spiff || 0) }}</td>
                  </tr>
                  <tr v-if="!itemDetailData.salespeople || !itemDetailData.salespeople.length">
                    <td colspan="9" class="text-center text-muted">No salesperson data available</td>
                  </tr>
                </tbody>
                <tfoot v-if="itemDetailData.salespeople && itemDetailData.salespeople.length">
                  <tr class="table-total">
                    <td><strong>TOTAL</strong></td>
                    <td class="text-right"><strong>{{ formatCurrency(itemDetailData.totalSales) }}</strong></td>
                    <td class="text-right"><strong>{{ formatCurrency(itemDetailData.netSales) }}</strong></td>
                    <td class="text-right"><strong>{{ formatCurrency(itemDetailData.regularSales) }}</strong></td>
                    <td class="text-right"><strong>{{ formatCurrency(itemDetailData.layawayTotal) }}</strong></td>
                    <td class="text-right"><strong>{{ formatCurrency(itemDetailData.layawayPaid) }}</strong></td>
                    <td class="text-right"><strong>{{ formatCurrency(itemDetailData.totalRefund) }}</strong></td>
                    <td class="text-right"><strong>{{ itemDetailData.orders }}</strong></td>
                    <td class="text-right spiff-value"><strong>{{ formatCurrency(itemDetailData.totalSpiff || 0) }}</strong></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </b-tab>

          <b-tab title="👥 Visits">
            <div class="visits-detail-grid">
              <div class="visits-stats-grid">
                <div class="visits-stat-card">
                  <span class="visits-stat-label">Total Visits</span>
                  <span class="visits-stat-value">{{ itemDetailData.visits || 0 }}</span>
                </div>
                <div class="visits-stat-card">
                  <span class="visits-stat-label">Unique</span>
                  <span class="visits-stat-value">{{ itemDetailData.unique_customers || 0 }}</span>
                </div>
                <div class="visits-stat-card">
                  <span class="visits-stat-label">Completed</span>
                  <span class="visits-stat-value">{{ itemDetailData.completed_visits || 0 }}</span>
                </div>
                <div class="visits-stat-card">
                  <span class="visits-stat-label">Active</span>
                  <span class="visits-stat-value">{{ itemDetailData.active_visits || 0 }}</span>
                </div>
              </div>

              <div class="visits-action-breakdown" v-if="itemDetailData.action_types && Object.keys(itemDetailData.action_types).length">
                <h6 class="section-title">📊 Action Types</h6>
                <div class="action-type-grid">
                  <div 
                    v-for="(count, type) in itemDetailData.action_types" 
                    :key="type"
                    class="action-type-card"
                  >
                    <span class="action-type-name">{{ type.replace('_', ' ').toUpperCase() }}</span>
                    <span class="action-type-count">{{ count }}</span>
                    <div class="action-type-bar">
                      <div 
                        class="action-type-bar-fill" 
                        :style="{ width: ((count / itemDetailData.visits) * 100) + '%' }"
                      ></div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="table-responsive-wrapper" style="max-height: 300px;">
                <table class="modal-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Check In</th>
                      <th>Action Type</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="visit in (itemDetailData.visit_list || [])" :key="visit.id">
                      <td>{{ visit.name || 'N/A' }}</td>
                      <td>{{ visit.email || 'N/A' }}</td>
                      <td>{{ formatDate(visit.check_in_time) }}</td>
                      <td>
                        <b-badge :variant="getActionTypeVariant(visit.action_type)" pill>
                          {{ visit.action_type || 'unknown' }}
                        </b-badge>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </b-tab>
        </b-tabs>
      </div>
    </b-modal>
  </div>
</template>

<script>
import axios from 'axios';
import moment from 'moment';
import Chart from 'chart.js';

export default {
  name: 'SalesReportDashboard',
  data() {
    const currentYear = new Date().getFullYear();
    const now = new Date();
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                        'July', 'August', 'September', 'October', 'November', 'December'];
    const currentMonthName = monthNames[now.getMonth()];
    
    return {
      isLoading: true,
      
      userDetails: {},
      locationOptions: [],
      
      periodCards: [],
      activePeriod: null,
      
      showPeriodModal: false,
      isPeriodModalLoading: false,
      periodModalTitle: '',
      periodModalData: null,
      transactionPage: 1,
      transactionPageSize: 25,
      visitPage: 1,
      visitPageSize: 25,
      
      showSalespersonDetail: false,
      isSalespersonDetailLoading: false,
      salespersonDetailTitle: '',
      salespersonDetailData: null,
      
      showViewModal: false,
      isViewModalLoading: false,
      viewModalTitle: '',
      viewType: 'daily',
      viewModalData: null,
      viewFilters: {
        month: currentMonthName,
        year: currentYear,
        week: 1,
        location: null,
        salesperson: null
      },
      
      showItemDetail: false,
      isItemDetailLoading: false,
      itemDetailTitle: '',
      itemDetailData: null,
      
      monthOptions: [
        { value: 'January', text: 'January' },
        { value: 'February', text: 'February' },
        { value: 'March', text: 'March' },
        { value: 'April', text: 'April' },
        { value: 'May', text: 'May' },
        { value: 'June', text: 'June' },
        { value: 'July', text: 'July' },
        { value: 'August', text: 'August' },
        { value: 'September', text: 'September' },
        { value: 'October', text: 'October' },
        { value: 'November', text: 'November' },
        { value: 'December', text: 'December' }
      ],
      yearOptions: [],
      
      statsPieCharts: {},
      paymentPieCharts: {},
      trendChart: null,
      
      chartColors: {
        purple: '#6366f1',
        purpleLight: 'rgba(99, 102, 241, 0.8)',
        purpleLighter: 'rgba(99, 102, 241, 0.2)',
        green: '#10b981',
        greenLight: 'rgba(16, 185, 129, 0.8)',
        greenLighter: 'rgba(16, 185, 129, 0.2)',
        blue: '#3b82f6',
        blueLight: 'rgba(59, 130, 246, 0.8)',
        yellow: '#f59e0b',
        yellowLight: 'rgba(245, 158, 11, 0.8)',
        red: '#ef4444',
        redLight: 'rgba(239, 68, 68, 0.8)',
        pink: '#ec4899',
        pinkLight: 'rgba(236, 72, 153, 0.8)',
        cyan: '#06b6d4',
        cyanLight: 'rgba(6, 182, 212, 0.8)',
        orange: '#f97316',
        orangeLight: 'rgba(249, 115, 22, 0.8)',
        indigo: '#8b5cf6',
        indigoLight: 'rgba(139, 92, 246, 0.8)',
        gray: '#94a3b8',
        grayLight: 'rgba(148, 163, 184, 0.8)',
        spiff: '#f59e0b',
        spiffLight: 'rgba(245, 158, 11, 0.2)'
      },
      
      colors: {
        'CASH': '#10b981',
        'CC': '#6366f1',
        'STORE_CREDIT': '#8b5cf6',
        'FINANCE': '#f59e0b',
        'TAX EXEMPT': '#06b6d4',
        'ACIMA CREDIT': '#f59e0b',
        'SNAP FINANCE': '#f59e0b',
        'AMERICAN FIRST': '#f59e0b',
        'WELLS FARGO': '#f59e0b',
        'PROGRESSIVE': '#f59e0b',
        'CHECK PERSONAL': '#f59e0b',
        'OTHER': '#64748b',
        'SPIFF': '#f59e0b'
      },
      
      actionTypeVariants: {
        'purchase': 'success',
        'estimate': 'info',
        'just_looking': 'secondary',
        'unknown': 'light'
      }
    };
  },
  computed: {
    weekOptions() {
      const monthIndex = this.monthOptions.findIndex(m => m.value === this.viewFilters.month);
      if (monthIndex === -1) return [];
      
      const year = this.viewFilters.year;
      const date = new Date(year, monthIndex, 1);
      const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();
      const firstDay = date.getDay();
      const weeks = Math.ceil((daysInMonth + firstDay) / 7);
      
      const options = [];
      for (let i = 1; i <= Math.min(weeks, 5); i++) {
        options.push({ value: i, text: `Week ${i}` });
      }
      return options;
    },
    
    viewLocationOptions() {
      if (!this.viewModalData || !this.viewModalData.data) {
        return [{ value: null, text: 'All Locations' }];
      }
      
      const locationSet = new Set();
      const data = this.viewModalData.data || [];
      
      data.forEach(item => {
        if (item.rawData) {
          if (item.rawData.location_id) {
            const locName = this.getLocationName(item.rawData.location_id);
            locationSet.add(JSON.stringify({ value: item.rawData.location_id, text: locName }));
          }
          if (item.rawData.location_summary) {
            Object.keys(item.rawData.location_summary).forEach(locId => {
              const locName = this.getLocationName(parseInt(locId));
              locationSet.add(JSON.stringify({ value: parseInt(locId), text: locName }));
            });
          }
        }
      });
      
      const options = Array.from(locationSet).map(str => JSON.parse(str));
      return [{ value: null, text: 'All Locations' }, ...options];
    },
    
    viewSalespersonOptions() {
      if (!this.viewModalData || !this.viewModalData.data) {
        return [{ value: null, text: 'All Salespeople' }];
      }
      
      const salespersonSet = new Set();
      const data = this.viewModalData.data || [];
      
      data.forEach(item => {
        if (item.rawData) {
          if (item.rawData.username) {
            salespersonSet.add(item.rawData.username);
          }
          if (item.rawData.salesperson_summary) {
            Object.keys(item.rawData.salesperson_summary).forEach(spName => {
              salespersonSet.add(spName);
            });
          }
        }
      });
      
      const options = Array.from(salespersonSet).map(name => ({
        value: name,
        text: name
      }));
      
      return [{ value: null, text: 'All Salespeople' }, ...options];
    },
    
    salespersonOptions() {
      if (!this.periodModalData || !this.periodModalData.salespeople) return [];
      return this.periodModalData.salespeople.map(sp => ({ value: sp.name, text: sp.name }));
    },
    
    paginatedTransactions() {
      if (!this.periodModalData || !this.periodModalData.transactions) return [];
      const start = (this.transactionPage - 1) * this.transactionPageSize;
      return this.periodModalData.transactions.slice(start, start + this.transactionPageSize);
    },
    
    paginatedVisits() {
      if (!this.periodModalData || !this.periodModalData.visits || !this.periodModalData.visits.list) return [];
      const start = (this.visitPage - 1) * this.visitPageSize;
      return this.periodModalData.visits.list.slice(start, start + this.visitPageSize);
    }
  },
  mounted() {
    this.loadUserData();
    this.generateYearOptions();
    this.initializeDashboard();
  },
  beforeDestroy() {
    Object.values(this.statsPieCharts).forEach(chart => {
      if (chart) chart.destroy();
    });
    Object.values(this.paymentPieCharts).forEach(chart => {
      if (chart) chart.destroy();
    });
    if (this.trendChart) {
      this.trendChart.destroy();
      this.trendChart = null;
    }
  },
  methods: {
    loadUserData() {
      try {
        this.userDetails = JSON.parse(localStorage.getItem('userDetails') || '{}');
        const locations = localStorage.getItem('locations');
        if (locations) {
          this.locationOptions = JSON.parse(locations).map(l => ({
            value: l.value,
            text: l.text
          }));
        }
        const packet = JSON.parse(localStorage.getItem('packet') || '{}');
        if (packet.locations) {
          this.locationOptions = packet.locations.map(l => ({
            value: l.value,
            text: l.text
          }));
        }
      } catch (e) {
        console.error('Error loading user data:', e);
      }
    },
    
    generateYearOptions() {
      const currentYear = new Date().getFullYear();
      for (let i = currentYear - 3; i <= currentYear + 3; i++) {
        this.yearOptions.push({ value: i, text: i.toString() });
      }
    },
    
    async initializeDashboard() {
      this.isLoading = true;
      await this.fetchAllPeriods();
      this.isLoading = false;
    },
    
    toggleFlip(key) {
      const period = this.periodCards.find(p => p.key === key);
      if (period) {
        period.flipped = !period.flipped;
        this.periodCards = [...this.periodCards];
        
        if (period.flipped) {
          this.$nextTick(() => {
            setTimeout(() => {
              this.renderCardCharts(key);
            }, 350);
          });
        }
      }
    },
    
    getLocationName(locationId) {
      if (!locationId) return 'Unknown';
      if (!this.locationOptions.length) return `Location ${locationId}`;
      const loc = this.locationOptions.find(l => l.value == locationId);
      return loc ? loc.text : `Location ${locationId}`;
    },
    
    getColor(method) {
      const financeMethods = ['ACIMA CREDIT', 'SNAP FINANCE', 'AMERICAN FIRST', 'WELLS FARGO', 'PROGRESSIVE', 'CHECK PERSONAL'];
      if (financeMethods.includes(method)) return this.colors['FINANCE'];
      if (method === 'SPIFF') return this.colors['SPIFF'];
      return this.colors[method] || this.colors['OTHER'];
    },
    
    getActionTypeVariant(type) {
      return this.actionTypeVariants[type] || 'secondary';
    },
    
    getPaymentMethodsString(paymentMethods) {
      if (!paymentMethods) return '-';
      if (Array.isArray(paymentMethods)) {
        return paymentMethods.join(', ');
      }
      if (typeof paymentMethods === 'string') {
        return paymentMethods;
      }
      return '-';
    },
    
    formatCurrency(value) {
      if (value === undefined || value === null) return '$0.00';
      return `$${parseFloat(value).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })}`;
    },
    
    formatDate(ts) {
      if (!ts) return '-';
      return moment(ts).format('MM-DD-YYYY HH:mm');
    },

    async fetchAllPeriods() {
      try {
        const params = {
          username: this.userDetails.username || 'admin',
          fetch_all_periods: true,
          include_spiff_details: true
        };
        
        const response = await axios.post('/api/reports/reports_optimized/', params, {
          headers: { 'Content-Type': 'application/json' }
        });
        
        if (response.data && response.data.result) {
          this.processPeriodData(response.data.result);
        }
      } catch (error) {
        console.error('Error fetching periods:', error);
        this.$bvToast.toast('Error loading period data', { title: 'Error', variant: 'danger' });
        this.setFallbackData();
      }
    },
    
    setFallbackData() {
      const periods = ['today', 'yesterday', 'this_week', 'last_week', 'this_month', 'last_month'];
      const labels = {
        today: 'Today',
        yesterday: 'Yesterday',
        this_week: 'This Week',
        last_week: 'Last Week',
        this_month: 'This Month',
        last_month: 'Last Month'
      };
      
      this.periodCards = periods.map(key => ({
        key: key,
        label: labels[key],
        totalSales: 0,
        netSales: 0,
        regularSales: 0,
        layawayTotal: 0,
        layawayPaid: 0,
        totalRefund: 0,
        salesRefund: 0,
        layawayRefund: 0,
        orders: 0,
        change: null,
        paymentMethods: [],
        visits: { total: 0, action_types: {}, unique_customers: 0, completed: 0, active: 0 },
        flipped: false,
        totalSpiff: 0,
        spiffInvoiceCount: 0
      }));
    },
    
    processPeriodData(data) {
      const periods = ['today', 'yesterday', 'this_week', 'last_week', 'this_month', 'last_month'];
      const labels = {
        today: 'Today',
        yesterday: 'Yesterday',
        this_week: 'This Week',
        last_week: 'Last Week',
        this_month: 'This Month',
        last_month: 'Last Month'
      };
      
      this.periodCards = periods.map(key => {
        const periodData = data[key] || {};
        const transactions = periodData.transactions || [];
        
        // Calculate SPIFF from transactions
        let totalSpiff = 0;
        let spiffInvoices = new Set();
        let spiffDetails = [];
        
        transactions.forEach(tx => {
          const spiffAmount = parseFloat(tx.spiff_commission) || 0;
          if (spiffAmount > 0) {
            totalSpiff += spiffAmount;
            spiffInvoices.add(tx.invoice_id);
            
            // Store spiff details for this invoice
            spiffDetails.push({
              invoice_id: tx.invoice_id,
              username: tx.username,
              location_id: tx.location_id,
              sale_date: tx.sale_date,
              invoice_total: tx.invoice_total || 0,
              spiff_value: spiffAmount,
              spiff_base_amount: tx.spiff_base_amount || 0,
              spiff_products_count: 1
            });
          }
        });
        
        // Aggregate spiff details by invoice
        const spiffDetailsMap = {};
        spiffDetails.forEach(detail => {
          const key = detail.invoice_id;
          if (!spiffDetailsMap[key]) {
            spiffDetailsMap[key] = {
              ...detail,
              spiff_products_count: 0
            };
          }
          spiffDetailsMap[key].spiff_products_count += 1;
        });
        
        const spiffDetailsArray = Object.values(spiffDetailsMap);
        
        // Extract SPIFF summary from the data or calculate from transactions
        const spiffSummary = periodData.spiff_summary || {};
        const totalSpiffValue = spiffSummary.total_spiff_value || totalSpiff;
        const spiffInvoiceCount = spiffSummary.spiff_invoice_count || spiffInvoices.size;
        
        return {
          key: key,
          label: labels[key],
          totalSales: periodData.grossSales || 0,
          netSales: periodData.netSales || 0,
          regularSales: periodData.regularSales || 0,
          layawayTotal: periodData.layawayTotal || 0,
          layawayPaid: periodData.layawayPaid || 0,
          totalRefund: periodData.totalRefund || 0,
          salesRefund: periodData.salesRefund || 0,
          layawayRefund: periodData.layawayRefund || 0,
          orders: periodData.count || 0,
          change: data[`${key}_change`] || null,
          paymentMethods: periodData.paymentStats || [],
          visits: {
            total: periodData.visits.total || 0,
            action_types: periodData.visits.action_types || {},
            unique_customers: periodData.visits.unique_customers || 0,
            completed: periodData.visits.completed || 0,
            active: periodData.visits.active || 0,
            list: periodData.visits.list || []
          },
          flipped: false,
          // SPIFF data - use calculated values
          totalSpiff: totalSpiffValue,
          spiffInvoiceCount: spiffInvoiceCount,
          spiffPercentageOfSales: spiffSummary.spiff_percentage_of_sales || 
            (periodData.grossSales > 0 ? (totalSpiffValue / periodData.grossSales) * 100 : 0),
          avgSpiffPerInvoice: spiffSummary.avg_spiff_per_invoice || 
            (spiffInvoiceCount > 0 ? totalSpiffValue / spiffInvoiceCount : 0),
          spiffInvoiceDetails: spiffSummary.spiff_invoice_details || spiffDetailsArray.slice(0, 10)
        };
      });
      
      this.$nextTick(() => {
        setTimeout(() => {
          this.periodCards.forEach(period => {
            this.renderCardCharts(period.key);
          });
        }, 100);
      });
    },
    
    renderCardCharts(key) {
      const period = this.periodCards.find(p => p.key === key);
      if (!period) return;
      
      setTimeout(() => {
        this.renderStatsPieChart(key, period);
        this.renderPaymentPieChart(key, period);
      }, 100);
    },
    
    renderStatsPieChart(key, period) {
      const canvas = document.getElementById('stats_pie_' + key);
      if (!canvas) return;
      
      if (this.statsPieCharts[key]) {
        this.statsPieCharts[key].destroy();
        delete this.statsPieCharts[key];
      }
      
      const regular = period.regularSales || 0;
      const layaway = period.layawayTotal || 0;
      const refunds = period.totalRefund || 0;
      const spiff = period.totalSpiff || 0;
      
      if (regular === 0 && layaway === 0 && refunds === 0 && spiff === 0) {
        this.statsPieCharts[key] = new Chart(canvas, {
          type: 'doughnut',
          data: {
            labels: ['No Data'],
            datasets: [{
              data: [1],
              backgroundColor: ['#e2e8f0'],
              borderWidth: 0
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: { legend: { display: false }, tooltip: { enabled: false } },
            cutout: '60%'
          }
        });
        return;
      }
      
      const data = [regular, layaway, refunds];
      const labels = ['Regular Sales', 'Layaway Sales', 'Refunds'];
      const colors = ['rgba(99, 102, 241, 0.8)', 'rgba(245, 158, 11, 0.8)', 'rgba(239, 68, 68, 0.8)'];
      const borderColors = ['#6366f1', '#f59e0b', '#ef4444'];
      
      if (spiff > 0) {
        data.push(spiff);
        labels.push('SPIFF');
        colors.push('rgba(245, 158, 11, 0.5)');
        borderColors.push('#f59e0b');
      }
      
      this.statsPieCharts[key] = new Chart(canvas, {
        type: 'doughnut',
        data: {
          labels: labels,
          datasets: [{
            data: data,
            backgroundColor: colors,
            borderColor: borderColors,
            borderWidth: 0.25,
            hoverBorderWidth: 1
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              display: false
            },
            tooltip: {
              backgroundColor: 'rgba(15, 23, 42, 0.92)',
              titleFont: { size: 10, weight: '600' },
              bodyFont: { size: 9 },
              padding: 6,
              cornerRadius: 4,
              callbacks: {
                label: (ctx) => {
                  const total = ctx.dataset.data.reduce((a, b) => a + b, 0);
                  const percentage = total > 0 ? ((ctx.parsed / total) * 100).toFixed(1) : 0;
                  return `${ctx.label}: ${this.formatCurrency(ctx.parsed)} (${percentage}%)`;
                }
              }
            }
          },
          cutout: '55%',
          animation: {
            animateRotate: true,
            duration: 600
          }
        }
      });
    },
    
    renderPaymentPieChart(key, period) {
      const canvas = document.getElementById('payment_pie_' + key);
      if (!canvas) return;
      
      if (this.paymentPieCharts[key]) {
        this.paymentPieCharts[key].destroy();
        delete this.paymentPieCharts[key];
      }
      
      const data = period.paymentMethods || [];
      
      if (!data.length) {
        this.paymentPieCharts[key] = new Chart(canvas, {
          type: 'doughnut',
          data: {
            labels: ['No Data'],
            datasets: [{
              data: [1],
              backgroundColor: ['#e2e8f0'],
              borderWidth: 0
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: { legend: { display: false }, tooltip: { enabled: false } },
            cutout: '60%'
          }
        });
        return;
      }
      
      const sortedData = [...data].sort((a, b) => b.amount - a.amount);
      const topData = sortedData.slice(0, 6);
      const otherData = sortedData.slice(6);
      
      const colorPalette = [
        '#10b981', '#6366f1', '#f59e0b', '#ef4444', 
        '#ec4899', '#06b6d4', '#8b5cf6', '#f97316'
      ];
      
      let labels = topData.map(d => d.method);
      let values = topData.map(d => d.amount);
      let colors = topData.map((d, i) => colorPalette[i % colorPalette.length]);
      
      if (otherData.length > 0) {
        const otherTotal = otherData.reduce((sum, d) => sum + d.amount, 0);
        labels.push('Other');
        values.push(otherTotal);
        colors.push('#94a3b8');
      }
      
      this.paymentPieCharts[key] = new Chart(canvas, {
        type: 'doughnut',
        data: {
          labels: labels,
          datasets: [{
            data: values,
            backgroundColor: colors,
            borderColor: '#ffffff',
            borderWidth: 0.25,
            hoverBorderWidth: 1
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              display: false
            },
            tooltip: {
              backgroundColor: 'rgba(15, 23, 42, 0.92)',
              titleFont: { size: 10, weight: '600' },
              bodyFont: { size: 9 },
              padding: 6,
              cornerRadius: 4,
              callbacks: {
                label: (ctx) => {
                  const total = ctx.dataset.data.reduce((a, b) => a + b, 0);
                  const percentage = total > 0 ? ((ctx.parsed / total) * 100).toFixed(1) : 0;
                  return `${ctx.label}: ${this.formatCurrency(ctx.parsed)} (${percentage}%)`;
                }
              }
            }
          },
          cutout: '50%',
          animation: {
            animateRotate: true,
            duration: 600
          }
        }
      });
    },

    async openPeriodModal(key) {
      this.activePeriod = key;
      const period = this.periodCards.find(p => p.key === key);
      if (!period) return;
      
      this.periodModalTitle = `${period.label} - Sales Details`;
      this.showPeriodModal = true;
      this.isPeriodModalLoading = true;
      this.transactionPage = 1;
      this.visitPage = 1;
      
      try {
        const params = {
          username: this.userDetails.username || 'admin',
          period: key,
          location_id: null,
          salesperson: null,
          include_spiff_details: true
        };
        
        const response = await axios.post('/api/reports/reports_period_detail/', params, {
          headers: { 'Content-Type': 'application/json' }
        });
        
        if (response.data && response.data.result) {
          this.periodModalData = response.data.result;
          
          // If SPIFF summary is missing, calculate from transactions
          if (!this.periodModalData.spiff_summary || this.periodModalData.spiff_summary.total_spiff_value === 0) {
            const transactions = this.periodModalData.transactions || [];
            let totalSpiff = 0;
            const spiffInvoices = new Set();
            const spiffDetails = [];
            
            transactions.forEach(tx => {
              const spiffAmount = parseFloat(tx.spiff_commission) || 0;
              if (spiffAmount > 0) {
                totalSpiff += spiffAmount;
                spiffInvoices.add(tx.invoice_id);
                spiffDetails.push({
                  invoice_id: tx.invoice_id,
                  username: tx.username,
                  location_id: tx.location_id,
                  sale_date: tx.sale_date || tx.ts,
                  invoice_total: tx.invoice_total || 0,
                  spiff_value: spiffAmount,
                  spiff_base_amount: tx.spiff_base_amount || 0,
                  spiff_products_count: 1
                });
              }
            });
            
            // Aggregate spiff details by invoice
            const spiffDetailsMap = {};
            spiffDetails.forEach(detail => {
              const key = detail.invoice_id;
              if (!spiffDetailsMap[key]) {
                spiffDetailsMap[key] = { ...detail, spiff_products_count: 0 };
              }
              spiffDetailsMap[key].spiff_products_count += 1;
            });
            
            this.periodModalData.spiff_summary = {
              spiff_invoice_count: spiffInvoices.size,
              total_spiff_value: totalSpiff,
              spiff_percentage_of_sales: this.periodModalData.totalSales > 0 ? (totalSpiff / this.periodModalData.totalSales) * 100 : 0,
              avg_spiff_per_invoice: spiffInvoices.size > 0 ? totalSpiff / spiffInvoices.size : 0,
              unique_spiff_products: 0,
              salespeople_with_spiffs: 0,
              locations_with_spiffs: 0,
              spiff_invoice_details: Object.values(spiffDetailsMap).slice(0, 20)
            };
          }
          
          // Add visits data from period card if not in response
          if (!this.periodModalData.visits) {
            this.periodModalData.visits = period.visits || { total: 0, action_types: {}, unique_customers: 0, completed: 0, active: 0, list: [] };
          }
        }
      } catch (error) {
        console.error('Error fetching period details:', error);
        this.$bvToast.toast('Error loading period details', { title: 'Error', variant: 'danger' });
      } finally {
        this.isPeriodModalLoading = false;
      }
    },
    
    onPeriodModalClose() {
      this.activePeriod = null;
      this.periodModalData = null;
    },

    openSalespersonDetail(salesperson) {
      this.salespersonDetailTitle = `${salesperson.name} - Sales Details`;
      this.showSalespersonDetail = true;
      this.isSalespersonDetailLoading = true;
      
      const transactions = this.periodModalData.transactions 
        ? this.periodModalData.transactions.filter(t => t.username === salesperson.name)
        : [];
      
      // Calculate total SPIFF for this salesperson
      let totalSpiff = 0;
      transactions.forEach(tx => {
        totalSpiff += parseFloat(tx.spiff_commission) || 0;
      });
      
      this.salespersonDetailData = {
        totalSales: salesperson.total_sales || 0,
        netSales: salesperson.net_sales || 0,
        regularSales: salesperson.regular_sales || 0,
        layawayTotal: salesperson.layaway_total || 0,
        layawayPaid: salesperson.layaway_paid || 0,
        totalRefund: salesperson.total_refund || 0,
        orders: salesperson.orders || 0,
        commission: salesperson.commission || 0,
        totalSpiff: totalSpiff || salesperson.spiff || 0,
        transactions: transactions,
        paymentMethods: this.calculatePaymentMethods(transactions),
        avgOrder: salesperson.orders > 0 ? (salesperson.net_sales || 0) / salesperson.orders : 0
      };
      
      this.isSalespersonDetailLoading = false;
    },
    
    calculatePaymentMethods(transactions) {
      const methods = {};
      transactions.forEach(t => {
        let paymentMethods = t.payment_methods;
        if (typeof paymentMethods === 'string') {
          paymentMethods = paymentMethods.split(',').map(m => m.trim());
        }
        if (Array.isArray(paymentMethods) && paymentMethods.length) {
          paymentMethods.forEach(m => {
            const amount = Math.abs(t.total_dollar_amount || 0);
            if (!methods[m]) methods[m] = { amount: 0, count: 0 };
            methods[m].amount += amount;
            methods[m].count += 1;
          });
        }
      });
      
      const total = Object.values(methods).reduce((sum, m) => sum + m.amount, 0) || 1;
      return Object.keys(methods).map(m => ({
        method: m,
        amount: methods[m].amount,
        count: methods[m].count,
        percentage: (methods[m].amount / total) * 100
      }));
    },

    openViewModal(viewType) {
      this.viewType = viewType;
      this.viewModalTitle = viewType.charAt(0).toUpperCase() + viewType.slice(1) + ' Sales';
      this.showViewModal = true;
      
      const now = new Date();
      const currentYear = now.getFullYear();
      const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                          'July', 'August', 'September', 'October', 'November', 'December'];
      const currentMonthName = monthNames[now.getMonth()];
      
      let currentWeek = Math.ceil(now.getDate() / 7);
      currentWeek = Math.min(Math.max(currentWeek, 1), 5);
      
      this.viewFilters = {
        month: currentMonthName,
        year: currentYear,
        week: currentWeek,
        location: null,
        salesperson: null
      };
      
      this.fetchViewData();
    },
    
    onViewModalShown() {
      setTimeout(() => {
        this.renderTrendChart();
      }, 300);
    },
    
    renderTrendChart() {
      const canvas = document.getElementById('trendChart');
      if (!canvas) return;
      
      if (this.trendChart) {
        this.trendChart.destroy();
        this.trendChart = null;
      }
      
      if (!this.viewModalData || !this.viewModalData.data || this.viewModalData.data.length === 0) {
        this.trendChart = new Chart(canvas, {
          type: 'line',
          data: {
            labels: ['No Data'],
            datasets: [{
              data: [0],
              backgroundColor: ['#e2e8f0'],
              borderColor: '#e2e8f0'
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: { legend: { display: false }, tooltip: { enabled: false } },
            scales: {
              y: { beginAtZero: true, grid: { display: false } },
              x: { grid: { display: false } }
            }
          }
        });
        return;
      }
      
      const labels = this.viewModalData.data.map(item => item.label);
      const totalData = this.viewModalData.data.map(item => item.totalSales || 0);
      const netData = this.viewModalData.data.map(item => item.netSales || 0);
      const refundData = this.viewModalData.data.map(item => Math.abs(item.totalRefund || 0));
      const spiffData = this.viewModalData.data.map(item => item.totalSpiff || 0);
      
      const datasets = [
        {
          label: 'Total Sales',
          data: totalData,
          borderColor: '#6366f1',
          backgroundColor: 'rgba(99, 102, 241, 0.1)',
          fill: true,
          tension: 0.4,
          pointBackgroundColor: '#6366f1',
          pointBorderColor: '#fff',
          pointBorderWidth: 1,
          pointRadius: 3,
          pointHoverRadius: 5,
          borderWidth: 2
        },
        {
          label: 'Net Sales',
          data: netData,
          borderColor: '#10b981',
          backgroundColor: 'rgba(16, 185, 129, 0.1)',
          fill: true,
          tension: 0.4,
          pointBackgroundColor: '#10b981',
          pointBorderColor: '#fff',
          pointBorderWidth: 1,
          pointRadius: 3,
          pointHoverRadius: 5,
          borderWidth: 2
        },
        {
          label: 'Refunds',
          data: refundData,
          borderColor: '#ef4444',
          backgroundColor: 'rgba(239, 68, 68, 0.05)',
          fill: true,
          tension: 0.4,
          pointBackgroundColor: '#ef4444',
          pointBorderColor: '#fff',
          pointBorderWidth: 1,
          pointRadius: 3,
          pointHoverRadius: 5,
          borderWidth: 2,
          borderDash: [5, 5]
        }
      ];
      
      if (spiffData.some(v => v > 0)) {
        datasets.push({
          label: 'SPIFF',
          data: spiffData,
          borderColor: '#f59e0b',
          backgroundColor: 'rgba(245, 158, 11, 0.1)',
          fill: true,
          tension: 0.4,
          pointBackgroundColor: '#f59e0b',
          pointBorderColor: '#fff',
          pointBorderWidth: 1,
          pointRadius: 3,
          pointHoverRadius: 5,
          borderWidth: 2,
          borderDash: [3, 3]
        });
      }
      
      this.trendChart = new Chart(canvas, {
        type: 'line',
        data: {
          labels: labels,
          datasets: datasets
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          interaction: {
            intersect: false,
            mode: 'index'
          },
          plugins: {
            legend: {
              display: false
            },
            tooltip: {
              backgroundColor: 'rgba(15, 23, 42, 0.92)',
              titleFont: { size: 12, weight: '600' },
              bodyFont: { size: 11 },
              padding: 10,
              cornerRadius: 8,
              callbacks: {
                label: (ctx) => {
                  return `${ctx.dataset.label}: ${this.formatCurrency(ctx.parsed.y)}`;
                }
              }
            }
          },
          scales: {
            y: {
              beginAtZero: true,
              grid: {
                color: 'rgba(0,0,0,0.06)',
                drawBorder: false
              },
              ticks: {
                font: { size: 10 },
                callback: (value) => this.formatCurrency(value)
              }
            },
            x: {
              grid: {
                display: false
              },
              ticks: {
                font: { size: 10, weight: '500' },
                color: '#64748b'
              }
            }
          },
          animation: {
            duration: 800,
            easing: 'easeOutQuart'
          }
        }
      });
    },
    
    async fetchViewData() {
      this.isViewModalLoading = true;
      
      try {
        const params = {
          username: this.userDetails.username || 'admin',
          view_type: this.viewType,
          month: this.viewFilters.month,
          year: this.viewFilters.year,
          week: this.viewFilters.week,
          location_id: this.viewFilters.location,
          salesperson: this.viewFilters.salesperson,
          include_spiff_details: true
        };
        
        const endpoint = `/api/reports/reports_${this.viewType}/`;
        const response = await axios.post(endpoint, params, {
          headers: { 'Content-Type': 'application/json' }
        });
        
        if (response.data && response.data.result) {
          this.viewModalData = this.processViewData(response.data.result);
          this.$nextTick(() => {
            setTimeout(() => {
              this.renderTrendChart();
            }, 200);
          });
        } else {
          this.viewModalData = null;
          this.$bvToast.toast('No data found for the selected filters', { 
            title: 'Info', 
            variant: 'info' 
          });
        }
      } catch (error) {
        console.error('Error fetching view data:', error);
        this.viewModalData = null;
        this.$bvToast.toast('Error loading data', { 
          title: 'Error', 
          variant: 'danger' 
        });
      } finally {
        this.isViewModalLoading = false;
      }
    },
    
    processViewData(data) {
      let items = [];
      let totals = {
        totalSales: 0,
        netSales: 0,
        regularSales: 0,
        layawayTotal: 0,
        layawayPaid: 0,
        totalRefund: 0,
        orders: 0,
        avgOrder: 0,
        totalVisits: 0,
        totalSpiff: 0
      };
      
      // Extract SPIFF summary from data or calculate
      const spiffSummary = data.spiff_summary || {};
      
      if (this.viewType === 'daily') {
        items = (data.daily_data || []).map(d => ({
          label: d.day_name,
          totalSales: d.total_sales,
          netSales: d.net_sales,
          regularSales: d.regular_sales,
          layawayTotal: d.layaway_total,
          layawayPaid: d.layaway_paid,
          totalRefund: d.total_refund,
          salesRefund: d.sales_refund,
          layawayRefund: d.layaway_refund,
          orders: d.order_count,
          avgOrder: d.avg_order,
          visits: d.visits || 0,
          totalSpiff: d.total_spiff || 0,
          rawData: d
        }));
        totals = data.weekly_totals || {};
        totals.totalVisits = data.total_visits || 0;
        totals.totalSpiff = spiffSummary.total_spiff_value || 0;
      } else if (this.viewType === 'weekly') {
        const allWeeks = (data.weekly_data || []).map(w => ({
          label: `Week ${w.week_number}`,
          totalSales: w.weekly_totals.total_sales,
          netSales: w.weekly_totals.net_sales,
          regularSales: w.weekly_totals.regular_sales,
          layawayTotal: w.weekly_totals.layaway_total,
          layawayPaid: w.weekly_totals.layaway_paid,
          totalRefund: w.weekly_totals.total_refund,
          salesRefund: w.weekly_totals.sales_refund,
          layawayRefund: w.weekly_totals.layaway_refund,
          orders: w.weekly_totals.order_count,
          avgOrder: w.weekly_totals.avg_order,
          visits: w.weekly_totals.visits || 0,
          totalSpiff: w.weekly_totals.total_spiff || 0,
          rawData: w
        }));
        
        const currentWeek = this.viewFilters.week || 1;
        const maxWeeks = 5;
        
        items = allWeeks.filter(w => {
          const weekNum = parseInt(w.label.split(' ')[1]);
          return (w.totalSales > 0 || w.orders > 0 || weekNum <= currentWeek) && weekNum <= maxWeeks;
        });
        
        totals = data.monthly_totals || {};
        totals.totalVisits = data.total_visits || 0;
        totals.totalSpiff = spiffSummary.total_spiff_value || 0;
      } else {
        items = (data.monthly_data || []).map(m => ({
          label: m.month,
          totalSales: m.monthly_totals.total_sales,
          netSales: m.monthly_totals.net_sales,
          regularSales: m.monthly_totals.regular_sales,
          layawayTotal: m.monthly_totals.layaway_total,
          layawayPaid: m.monthly_totals.layaway_paid,
          totalRefund: m.monthly_totals.total_refund,
          salesRefund: m.monthly_totals.sales_refund,
          layawayRefund: m.monthly_totals.layaway_refund,
          orders: m.monthly_totals.order_count,
          avgOrder: m.monthly_totals.avg_order,
          visits: m.monthly_totals.visits || 0,
          totalSpiff: m.monthly_totals.total_spiff || 0,
          rawData: m
        }));
        totals = data.yearly_totals || {};
        totals.totalVisits = data.total_visits || 0;
        totals.totalSpiff = spiffSummary.total_spiff_value || 0;
      }
      
      return {
        data: items,
        totalSales: totals.total_sales || 0,
        netSales: totals.net_sales || 0,
        regularSales: totals.regular_sales || 0,
        layawayTotal: totals.layaway_total || 0,
        layawayPaid: totals.layaway_paid || 0,
        totalRefund: totals.total_refund || 0,
        salesRefund: totals.sales_refund || 0,
        layawayRefund: totals.layaway_refund || 0,
        totalOrders: totals.order_count || 0,
        avgOrder: totals.avg_order || 0,
        totalVisits: totals.totalVisits || 0,
        totalSpiff: totals.totalSpiff || 0,
        spiff_summary: spiffSummary,
        rawData: data
      };
    },

    openItemDetail(item, viewType) {
      this.itemDetailTitle = `${item.label} - Sales Details`;
      this.showItemDetail = true;
      this.isItemDetailLoading = true;
      
      const rawData = item.rawData || {};
      const fullData = this.viewModalData.rawData || {};
      
      let locations = [];
      let salespeople = [];
      let paymentMethods = [];
      
      if (rawData.payment_methods && Array.isArray(rawData.payment_methods)) {
        paymentMethods = rawData.payment_methods;
      } else if (rawData.paymentMethods && Array.isArray(rawData.paymentMethods)) {
        paymentMethods = rawData.paymentMethods;
      }
      
      if (!paymentMethods.length && fullData.payment_methods) {
        const pmData = fullData.payment_methods;
        if (typeof pmData === 'object') {
          const totalAmount = Object.values(pmData).reduce((sum, p) => sum + (p.amount || 0), 0);
          paymentMethods = Object.keys(pmData).map(key => ({
            method: key,
            amount: pmData[key].amount || 0,
            count: pmData[key].count || 0,
            percentage: totalAmount > 0 ? ((pmData[key].amount || 0) / totalAmount * 100) : 0
          }));
        }
      }
      
      const extractLocations = (summary) => {
        if (!summary) return [];
        return Object.keys(summary).map(key => ({
          id: key,
          name: this.getLocationName(parseInt(key)),
          total_sales: summary[key].total_sales || 0,
          net_sales: summary[key].net_sales || 0,
          regular_sales: summary[key].regular_sales || 0,
          layaway_total: summary[key].layaway_total || 0,
          layaway_paid: summary[key].layaway_paid || 0,
          total_refund: summary[key].total_refund || 0,
          orders: summary[key].order_count || 0,
          visits: summary[key].visits || 0,
          spiff: summary[key].spiff || 0
        }));
      };
      
      const extractSalespeople = (summary) => {
        if (!summary) return [];
        return Object.keys(summary).map(key => ({
          name: key,
          total_sales: summary[key].total_sales || 0,
          net_sales: summary[key].net_sales || 0,
          regular_sales: summary[key].regular_sales || 0,
          layaway_total: summary[key].layaway_total || 0,
          layaway_paid: summary[key].layaway_paid || 0,
          total_refund: summary[key].total_refund || 0,
          orders: summary[key].order_count || 0,
          spiff: summary[key].spiff || 0
        }));
      };
      
      if (viewType === 'daily') {
        const dailyData = fullData.daily_data || [];
        const dayData = dailyData.find(d => d.day_name === item.label);
        if (dayData) {
          const locSummary = fullData.location_summary || {};
          locations = extractLocations(locSummary);
          const spSummary = fullData.salesperson_summary || {};
          salespeople = extractSalespeople(spSummary);
        }
      } else if (viewType === 'weekly') {
        const weeklyData = fullData.weekly_data || [];
        const weekData = weeklyData.find(w => w.week_number === parseInt(item.label.split(' ')[1]));
        if (weekData) {
          const locSummary = weekData.location_summary || {};
          locations = extractLocations(locSummary);
          const spSummary = weekData.salesperson_summary || {};
          salespeople = extractSalespeople(spSummary);
        }
      } else if (viewType === 'monthly') {
        const monthlyData = fullData.monthly_data || [];
        const monthData = monthlyData.find(m => m.month === item.label);
        
        if (monthData) {
          const locMap = {};
          const spMap = {};
          const weeks = monthData.weekly_data || [];
          
          if (weeks.length > 0) {
            weeks.forEach(week => {
              const locSummary = week.location_summary || {};
              Object.keys(locSummary).forEach(key => {
                if (!locMap[key]) {
                  locMap[key] = {
                    total_sales: 0,
                    net_sales: 0,
                    regular_sales: 0,
                    layaway_total: 0,
                    layaway_paid: 0,
                    total_refund: 0,
                    order_count: 0,
                    visits: 0,
                    spiff: 0
                  };
                }
                locMap[key].total_sales += locSummary[key].total_sales || 0;
                locMap[key].net_sales += locSummary[key].net_sales || 0;
                locMap[key].regular_sales += locSummary[key].regular_sales || 0;
                locMap[key].layaway_total += locSummary[key].layaway_total || 0;
                locMap[key].layaway_paid += locSummary[key].layaway_paid || 0;
                locMap[key].total_refund += locSummary[key].total_refund || 0;
                locMap[key].order_count += locSummary[key].order_count || 0;
                locMap[key].visits += locSummary[key].visits || 0;
                locMap[key].spiff += locSummary[key].spiff || 0;
              });
              
              const spSummary = week.salesperson_summary || {};
              Object.keys(spSummary).forEach(key => {
                if (!spMap[key]) {
                  spMap[key] = {
                    total_sales: 0,
                    net_sales: 0,
                    regular_sales: 0,
                    layaway_total: 0,
                    layaway_paid: 0,
                    total_refund: 0,
                    order_count: 0,
                    spiff: 0
                  };
                }
                spMap[key].total_sales += spSummary[key].total_sales || 0;
                spMap[key].net_sales += spSummary[key].net_sales || 0;
                spMap[key].regular_sales += spSummary[key].regular_sales || 0;
                spMap[key].layaway_total += spSummary[key].layaway_total || 0;
                spMap[key].layaway_paid += spSummary[key].layaway_paid || 0;
                spMap[key].total_refund += spSummary[key].total_refund || 0;
                spMap[key].order_count += spSummary[key].order_count || 0;
                spMap[key].spiff += spSummary[key].spiff || 0;
              });
            });
            
            if (monthData.payment_methods) {
              const pmData = monthData.payment_methods;
              if (typeof pmData === 'object') {
                const totalAmount = Object.values(pmData).reduce((sum, p) => sum + (p.amount || 0), 0);
                paymentMethods = Object.keys(pmData).map(key => ({
                  method: key,
                  amount: pmData[key].amount || 0,
                  count: pmData[key].count || 0,
                  percentage: totalAmount > 0 ? ((pmData[key].amount || 0) / totalAmount * 100) : 0
                }));
              }
            }
          }
          
          locations = Object.keys(locMap).map(key => ({
            id: key,
            name: this.getLocationName(parseInt(key)),
            total_sales: locMap[key].total_sales,
            net_sales: locMap[key].net_sales,
            regular_sales: locMap[key].regular_sales,
            layaway_total: locMap[key].layaway_total,
            layaway_paid: locMap[key].layaway_paid,
            total_refund: locMap[key].total_refund,
            orders: locMap[key].order_count,
            visits: locMap[key].visits,
            spiff: locMap[key].spiff
          }));
          
          salespeople = Object.keys(spMap).map(key => ({
            name: key,
            total_sales: spMap[key].total_sales,
            net_sales: spMap[key].net_sales,
            regular_sales: spMap[key].regular_sales,
            layaway_total: spMap[key].layaway_total,
            layaway_paid: spMap[key].layaway_paid,
            total_refund: spMap[key].total_refund,
            orders: spMap[key].order_count,
            spiff: spMap[key].spiff
          }));
        }
      }
      
      locations.sort((a, b) => b.total_sales - a.total_sales);
      salespeople.sort((a, b) => b.total_sales - a.total_sales);
      
      const newLayaway = (item.layawayTotal || 0) - (item.layawayPaid || 0);
      
      this.itemDetailData = {
        totalSales: item.totalSales || 0,
        netSales: item.netSales || 0,
        regularSales: item.regularSales || 0,
        layawayTotal: item.layawayTotal || 0,
        newLayaway: newLayaway,
        layawayPaid: item.layawayPaid || 0,
        totalRefund: item.totalRefund || 0,
        salesRefund: item.salesRefund || 0,
        layawayRefund: item.layawayRefund || 0,
        orders: item.orders || 0,
        avgOrder: item.avgOrder || 0,
        visits: item.visits || 0,
        totalSpiff: item.totalSpiff || 0,
        paymentMethods: paymentMethods,
        salespeople: salespeople,
        locations: locations,
        action_types: item.rawData.action_types || {},
        unique_customers: item.rawData.unique_customers || 0,
        completed_visits: item.rawData.completed_visits || 0,
        active_visits: item.rawData.active_visits || 0,
        visit_list: item.rawData.visit_list || []
      };
      
      this.isItemDetailLoading = false;
    }
  }
};
</script>

<style scoped>
.sales-dashboard {
  padding: 16px;
  background: #f1f5f9;
  min-height: 100vh;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
}

/* ===== 3D CUBE LOADER ===== */
.global-loader {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 80vh;
  background: #f1f5f9;
}

.loader-3d {
  width: 70px;
  height: 70px;
  perspective: 600px;
}

.loader-3d.small {
  width: 40px;
  height: 40px;
}

.cube {
  width: 100%;
  height: 100%;
  position: relative;
  transform-style: preserve-3d;
  animation: rotateCube 2s infinite ease-in-out;
}

@keyframes rotateCube {
  0% { transform: rotateX(-30deg) rotateY(0deg); }
  50% { transform: rotateX(-30deg) rotateY(180deg); }
  100% { transform: rotateX(-30deg) rotateY(360deg); }
}

.cube .face {
  position: absolute;
  width: 100%;
  height: 100%;
  border: 2px solid rgba(99, 102, 241, 0.3);
  border-radius: 6px;
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.8), rgba(139, 92, 246, 0.8));
  box-shadow: 0 0 30px rgba(99, 102, 241, 0.2);
}

.cube .face.front { transform: translateZ(calc(100% / 2)); }
.cube .face.back { transform: rotateY(180deg) translateZ(calc(100% / 2)); }
.cube .face.right { transform: rotateY(90deg) translateZ(calc(100% / 2)); }
.cube .face.left { transform: rotateY(-90deg) translateZ(calc(100% / 2)); }
.cube .face.top { transform: rotateX(90deg) translateZ(calc(100% / 2)); }
.cube .face.bottom { transform: rotateX(-90deg) translateZ(calc(100% / 2)); }

.loader-text {
  margin-top: 20px;
  color: #64748b;
  font-size: 14px;
  animation: pulseText 1.5s ease-in-out infinite;
}

@keyframes pulseText {
  0%, 100% { opacity: 0.6; }
  50% { opacity: 1; }
}

/* ===== HEADER ===== */
.dashboard-header {
  background: linear-gradient(135deg, #0f172a, #1e293b);
  border-radius: 16px;
  padding: 20px 28px;
  margin-bottom: 20px;
  box-shadow: 0 4px 24px rgba(0,0,0,0.15);
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 16px;
}

.header-icon {
  width: 44px;
  height: 44px;
  background: rgba(99, 102, 241, 0.2);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #818cf8;
}

.header-title {
  font-size: 20px;
  font-weight: 700;
  color: white;
  margin: 0;
  letter-spacing: -0.5px;
}

.header-subtitle {
  font-size: 13px;
  color: rgba(255,255,255,0.6);
  margin: 0;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 16px;
}

.header-badge {
  display: flex;
  align-items: center;
  gap: 8px;
  background: rgba(16, 185, 129, 0.15);
  color: #34d399;
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
}

.badge-dot {
  width: 8px;
  height: 8px;
  background: #34d399;
  border-radius: 50%;
  animation: pulse-dot 2s infinite;
}

@keyframes pulse-dot {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.3; }
}

.header-user {
  display: flex;
  align-items: center;
  gap: 8px;
  color: rgba(255,255,255,0.8);
  font-size: 13px;
}

/* ===== FLIP CARDS ===== */
.period-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin-bottom: 20px;
}

.flip-card {
  perspective: 1000px;
  height: 305px;
}

.flip-card-inner {
  position: relative;
  width: 100%;
  height: 100%;
  transition: transform 0.8s cubic-bezier(0.4, 0, 0.2, 1);
  transform-style: preserve-3d;
}

.flip-card.flipped .flip-card-inner {
  transform: rotateY(180deg);
}

.flip-card-front,
.flip-card-back {
  position: absolute;
  width: 100%;
  height: 100%;
  backface-visibility: hidden;
  border-radius: 16px;
  padding: 16px 18px;
  display: flex;
  flex-direction: column;
  box-shadow: 0 2px 12px rgba(0,0,0,0.06);
  border: 2px solid transparent;
  transition: border-color 0.3s;
  cursor: pointer;
}

.flip-card-front {
  background: white;
  transform: rotateY(0deg);
}

.flip-card-back {
  background: white;
  transform: rotateY(180deg);
}

.flip-card.active .flip-card-front,
.flip-card.active .flip-card-back {
  border-color: #6366f1;
  background: linear-gradient(135deg, #f8faff, #eef2ff);
}

.flip-card-front:hover,
.flip-card-back:hover {
  box-shadow: 0 8px 30px rgba(0,0,0,0.1);
}

/* Front Side Styles */
.flip-card-header {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 8px;
  gap: 8px;
}

.flip-label {
  font-size: 16px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #64748b;
  font-weight: 700;
}

.period-change {
  font-size: 10px;
  font-weight: 600;
  padding: 2px 10px;
  border-radius: 12px;
}

.period-change.positive {
  color: #10b981;
  background: #d1fae5;
}

.period-change.negative {
  color: #ef4444;
  background: #fecaca;
}

.flip-main-stats {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 8px;
  margin-bottom: 8px;
}

.flip-stat {
  background: #f8fafc;
  border-radius: 10px;
  padding: 6px 12px;
  text-align: center;
  border: 1px solid #eef2ff;
}

.flip-stat.spiff-stat {
  background: linear-gradient(135deg, #fffbeb, #fef3c7) !important;
  border-color: #f59e0b !important;
}

.flip-stat-label {
  display: block;
  font-size: 9px;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  font-weight: 600;
}

.flip-stat-value {
  display: block;
  font-size: 20px;
  font-weight: 700;
  line-height: 1.2;
  margin-top: 2px;
}

.flip-stat-value.primary { color: #2c2c2c; }
.flip-stat-value.success { color: #10b981; }
.flip-stat-value.spiff { color: #d97706; }

.flip-stat-sub {
  display: block;
  font-size: 9px;
  color: #92400e;
  font-weight: 500;
}

.flip-detail-grid {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 6px;
  margin-bottom: 6px;
  flex: 1;
}

.flip-detail-card {
  text-align: center;
  background: #f8fafc;
  border-radius: 6px;
  padding: 4px 8px;
  display: flex;
  flex-direction: column;
  border: 1px solid #f1f5f9;
}

.flip-detail-label {
  font-size: 8px;
  color: #94a3b8;
  font-weight: 500;
  margin-bottom: 1px;
}

.flip-detail-value {
  font-size: 17px;
  font-weight: 700;
  color: #36393d;
}

.flip-detail-value.text-danger {
  color: #ef4444;
}

.flip-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 6px;
  border-top: 1px solid #f1f5f9;
  flex-wrap: wrap;
  gap: 4px;
}

.flip-orders {
  font-size: 10px;
  color: #94a3b8;
  font-weight: 500;
}

.flip-visits-footer {
  font-size: 11px;
  color: #0369a1;
  font-weight: 500;
}

.flip-spiff-footer {
  font-size: 11px;
  color: #d97706;
  font-weight: 600;
}

.flip-actions {
  display: flex;
  align-items: center;
  gap: 6px;
}

.flip-hint {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 10px;
  color: #6366f1;
  font-weight: 500;
  background: rgba(99, 102, 241, 0.08);
  padding: 3px 10px;
  border-radius: 16px;
  transition: all 0.3s;
  cursor: pointer;
}

.flip-hint.back {
  background: rgba(16, 185, 129, 0.08);
  color: #10b981;
}

.flip-hint:hover {
  background: rgba(99, 102, 241, 0.15);
  transform: scale(1.05);
}

.flip-hint.back:hover {
  background: rgba(16, 185, 129, 0.15);
}

.flip-hint-icon {
  font-size: 12px;
  animation: spinHint 3s linear infinite;
}

@keyframes spinHint {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* ===== BACK SIDE - Charts ===== */
.flip-back-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
}

.flip-back-label {
  font-size: 13px;
  font-weight: 600;
  color: #1e293b;
}

.flip-charts-layout {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  flex: 1;
  min-height: 0;
}

.flip-chart-section {
  display: flex;
  flex-direction: column;
  background: #f8fafc;
  border-radius: 10px;
  padding: 6px 8px;
  min-height: 0;
}

.flip-chart-title {
  font-size: 9px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  text-align: center;
  margin-bottom: 3px;
}

.flip-chart-wrapper {
  flex: 1;
  min-height: 0;
  position: relative;
}

.flip-chart {
  width: 100% !important;
  height: 100% !important;
  max-height: 110px;
}

.flip-chart-stats {
  display: flex;
  flex-direction: column;
  gap: 1px;
  margin-top: 3px;
  padding-top: 3px;
  border-top: 1px solid #e2e8f0;
}

.flip-chart-stats.payment-stats {
  max-height: 50px;
  overflow-y: auto;
}

.stats-label-item {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 8px;
  color: #1e293b;
  padding: 1px 0;
}

.stats-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  display: inline-block;
  flex-shrink: 0;
}

.stats-dot.regular-dot { background: #6366f1; }
.stats-dot.layaway-dot { background: #f59e0b; }
.stats-dot.refund-dot { background: #ef4444; }
.stats-dot.spiff-dot { background: #f59e0b; }

.stats-label-text {
  flex: 1;
  font-weight: 300;
  font-size: 14px;
  color: #475569;
}

.stats-label-value {
  font-weight: 300;
  color: #1e293b;
  font-size: 14px;
}

.stats-label-value.text-danger {
  color: #ef4444;
}

.stats-label-value.spiff {
  color: #d97706;
  font-weight: 700;
}

.more-methods {
  color: #94a3b8;
  font-style: italic;
  justify-content: center;
  font-size: 8px;
}

.text-muted {
  color: #94a3b8;
}

.flip-back-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 6px;
  border-top: 1px solid #f1f5f9;
  margin-top: 4px;
  flex-wrap: wrap;
  gap: 4px;
}

.flip-payment-count {
  font-size: 10px;
  color: #94a3b8;
}

/* ===== MAIN CARDS ===== */
.main-cards-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin-bottom: 20px;
}

.main-card {
  background: white;
  border-radius: 16px;
  padding: 18px 22px;
  display: flex;
  align-items: center;
  gap: 16px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.06);
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  border: 2px solid transparent;
}

.main-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 40px rgba(0,0,0,0.1);
  border-color: #6366f1;
}

.main-card-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  flex-shrink: 0;
}

.main-card-icon.daily { background: linear-gradient(135deg, #6366f1, #8b5cf6); }
.main-card-icon.weekly { background: linear-gradient(135deg, #10b981, #34d399); }
.main-card-icon.monthly { background: linear-gradient(135deg, #f59e0b, #fbbf24); }

.main-card-content h6 {
  margin: 0;
  font-weight: 600;
  color: #1e293b;
  font-size: 14px;
}

.main-card-content p {
  margin: 2px 0 0;
  font-size: 12px;
  color: #64748b;
}

.main-card-cta {
  display: inline-block;
  font-size: 11px;
  color: #6366f1;
  font-weight: 600;
  margin-top: 4px;
  transition: transform 0.3s;
}

.main-card:hover .main-card-cta {
  transform: translateX(4px);
}

/* ===== MODAL LOADER ===== */
.modal-loader {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
}

/* ===== MODAL STYLES ===== */
.period-modal-header,
.view-modal-header,
.detail-modal-header {
  background: linear-gradient(135deg, #0f172a, #1e293b);
  color: white;
  border-bottom: none;
  padding: 14px 24px;
}

.period-modal-header .close,
.view-modal-header .close,
.detail-modal-header .close {
  color: white;
  opacity: 0.8;
}

.period-modal-header .close:hover,
.view-modal-header .close:hover,
.detail-modal-header .close:hover {
  opacity: 1;
  transform: rotate(90deg);
}

.period-modal-body,
.view-modal-body,
.detail-modal-body {
  padding: 18px 22px;
  background: #f8fafc;
}

/* Summary Grid */
.modal-summary-grid {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 10px;
  margin-bottom: 16px;
}

.modal-summary-item {
  background: white;
  padding: 12px 14px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  gap: 10px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
}

.modal-summary-item .summary-icon {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 16px;
  flex-shrink: 0;
}

.summary-icon.total { background: linear-gradient(135deg, #6366f1, #8b5cf6); }
.summary-icon.net { background: linear-gradient(135deg, #10b981, #34d399); }
.summary-icon.regular { background: linear-gradient(135deg, #06b6d4, #22d3ee); }
.summary-icon.layaway { background: linear-gradient(135deg, #f59e0b, #fbbf24); }
.summary-icon.refund { background: linear-gradient(135deg, #ef4444, #f87171); }
.summary-icon.orders { background: linear-gradient(135deg, #8b5cf6, #a78bfa); }

.modal-summary-item .label {
  display: block;
  font-size: 9px;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.modal-summary-item .value {
  display: block;
  font-size: 16px;
  font-weight: 700;
  color: #1e293b;
  line-height: 1.2;
}

.modal-summary-item .value.text-danger {
  color: #ef4444;
}

.modal-summary-item small {
  font-size: 9px;
  color: #94a3b8;
}

/* ===== SPIFF Summary Section ===== */
.spiff-summary-section {
  background: linear-gradient(135deg, #fffbeb, #fef3c7);
  border-radius: 12px;
  padding: 14px 18px;
  margin-bottom: 16px;
  border: 1px solid #fde68a;
}

.spiff-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
}

.spiff-icon {
  font-size: 20px;
}

.spiff-title {
  font-size: 14px;
  font-weight: 700;
  color: #92400e;
}

.spiff-badge {
  margin-left: auto;
  background: #f59e0b;
  color: white;
  padding: 2px 12px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
}

.spiff-grid {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 8px;
}

.spiff-item {
  background: white;
  padding: 8px 12px;
  border-radius: 8px;
  text-align: center;
}

.spiff-item .spiff-label {
  display: block;
  font-size: 9px;
  color: #92400e;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.spiff-item .spiff-value {
  display: block;
  font-size: 14px;
  font-weight: 700;
  color: #d97706;
}

/* ===== SPIFF Details Section ===== */
.spiff-details-section {
  margin-bottom: 16px;
}

.spiff-details-section .spiff-value {
  font-weight: 700;
  color: #d97706;
}

/* ===== SPIFF Summary Cards (for SPIFF Details Tab) ===== */
.spiff-summary-cards {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 10px;
  margin-bottom: 16px;
}

.spiff-card {
  background: white;
  padding: 12px;
  border-radius: 8px;
  text-align: center;
  border: 1px solid #fde68a;
}

.spiff-card .spiff-card-label {
  display: block;
  font-size: 9px;
  color: #92400e;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.spiff-card .spiff-card-value {
  display: block;
  font-size: 16px;
  font-weight: 700;
  color: #d97706;
}

.spiff-invoice-table {
  margin-top: 12px;
}

/* ===== SPIFF Mini Summary in View Modal ===== */
.spiff-summary-mini {
  background: linear-gradient(135deg, #fffbeb, #fef3c7);
  border-radius: 10px;
  padding: 10px 14px;
  margin-bottom: 14px;
  border: 1px solid #fde68a;
}

.spiff-mini-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
}

.spiff-mini-item {
  text-align: center;
}

.spiff-mini-item .spiff-mini-label {
  display: block;
  font-size: 9px;
  color: #92400e;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.spiff-mini-item .spiff-mini-value {
  display: block;
  font-size: 14px;
  font-weight: 700;
  color: #d97706;
}

/* ===== SPIFF Value Class ===== */
.spiff-value {
  color: #d97706 !important;
  font-weight: 700;
}

.spiff-highlight {
  background: linear-gradient(135deg, #fffbeb, #fef3c7) !important;
  border: 1px solid #f59e0b;
}

/* ===== Legend Dot for SPIFF ===== */
.legend-dot.spiff-dot {
  background: #f59e0b;
}

/* ===== Tabs ===== */
.modal-tabs >>> .nav-tabs .nav-link {
  border: none;
  color: #64748b;
  font-weight: 500;
  padding: 8px 16px;
  border-radius: 8px;
}

.modal-tabs >>> .nav-tabs .nav-link.active {
  background: #6366f1;
  color: white;
}

.modal-tabs >>> .tab-content {
  padding: 16px 0 0;
}

/* ===== Payment Methods ===== */
.payment-methods-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 12px;
}

.payment-methods-grid.small {
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
}

.payment-method-card {
  background: white;
  border-radius: 10px;
  padding: 12px 14px;
  border: 1px solid #e2e8f0;
}

.payment-method-card.small {
  padding: 8px 12px;
}

.payment-method-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
}

.payment-method-name {
  font-weight: 600;
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 6px;
}

.method-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  display: inline-block;
}

.payment-method-amount {
  font-weight: 700;
  font-size: 13px;
  color: #1e293b;
}

.payment-bar {
  height: 4px;
  background: #e2e8f0;
  border-radius: 4px;
  margin: 4px 0;
  overflow: hidden;
}

.payment-bar-fill {
  height: 100%;
  border-radius: 4px;
  transition: width 0.8s ease;
}

.payment-method-footer {
  display: flex;
  justify-content: space-between;
  font-size: 10px;
  color: #64748b;
  margin-top: 2px;
}

.sub-methods {
  margin-top: 6px;
  padding: 4px 8px;
  background: #f1f5f9;
  border-radius: 6px;
}

.sub-method {
  display: flex;
  justify-content: space-between;
  font-size: 11px;
  padding: 2px 0;
}

.sub-method-name {
  color: #475569;
}

.sub-method-amount {
  font-weight: 500;
  color: #1e293b;
}

/* ===== Tables ===== */
.table-responsive-wrapper {
  overflow-x: auto;
  max-height: 350px;
  overflow-y: auto;
  border-radius: 10px;
}

.modal-table {
  width: 100%;
  font-size: 12px;
  border-collapse: collapse;
}

.modal-table th,
.modal-table td {
  padding: 8px 12px;
  border-bottom: 1px solid #f1f5f9;
  white-space: nowrap;
}

.modal-table th {
  background: #f1f5f9;
  font-weight: 600;
  color: #475569;
  position: sticky;
  top: 0;
  z-index: 1;
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.modal-table tbody tr:hover {
  background: #f1f5f9;
}

.modal-table .table-total {
  background: #f1f5f9;
  font-weight: 700;
}

.modal-table .table-total td {
  border-top: 2px solid #e2e8f0;
}

.modal-pagination {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0 0;
  border-top: 1px solid #e2e8f0;
  margin-top: 12px;
}

/* ===== View Filters ===== */
.view-filters {
  background: white;
  border-radius: 12px;
  padding: 16px 20px;
  margin-bottom: 16px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
}

.filters-row {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: flex-end;
}

.filter-group {
  display: flex;
  flex-direction: column;
  min-width: 120px;
  flex: 1;
}

.filter-group label {
  font-size: 10px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  margin-bottom: 2px;
}

.filter-group .form-control,
.filter-group .custom-select {
  border-radius: 8px;
  font-size: 12px;
  background: #f8fafc;
  border-color: #e2e8f0;
}

.filter-group.filter-actions {
  flex: 0 0 auto;
}

.view-summary-grid {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 10px;
  margin-bottom: 16px;
}

.view-summary-item {
  background: #f8fafc;
  padding: 10px 14px;
  border-radius: 8px;
  text-align: center;
}

.view-summary-item .label {
  display: block;
  font-size: 10px;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.view-summary-item .value {
  display: block;
  font-size: 16px;
  font-weight: 700;
  color: #1e293b;
}

.view-summary-item .value.text-danger {
  color: #ef4444;
}

/* ===== Trend Chart ===== */
.trend-chart-container {
  background: white;
  border-radius: 12px;
  padding: 16px 20px;
  margin-bottom: 16px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
}

.trend-chart-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.trend-chart-title {
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  display: flex;
  align-items: center;
}

.trend-chart-legend {
  display: flex;
  gap: 16px;
}

.legend-item {
  font-size: 11px;
  color: #64748b;
  display: flex;
  align-items: center;
  gap: 6px;
}

.legend-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  display: inline-block;
}

.legend-dot.total-dot { background: #6366f1; }
.legend-dot.net-dot { background: #10b981; }
.legend-dot.refund-dot { background: #ef4444; }
.legend-dot.spiff-dot { background: #f59e0b; }

.trend-chart-wrapper {
  height: 220px;
  width: 100%;
}

/* ===== Detail Summary ===== */
.detail-summary-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  margin-bottom: 16px;
}

.detail-summary-item {
  background: #f8fafc;
  padding: 10px 14px;
  border-radius: 8px;
  text-align: center;
}

.detail-summary-item .label {
  display: block;
  font-size: 10px;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.detail-summary-item .value {
  display: block;
  font-size: 16px;
  font-weight: 700;
  color: #1e293b;
}

.detail-summary-grid-expanded {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 10px;
  margin-bottom: 16px;
}

.detail-summary-grid-expanded .detail-summary-item {
  background: #f8fafc;
  padding: 10px 14px;
  border-radius: 8px;
  text-align: center;
}

.detail-summary-grid-expanded .detail-summary-item .label {
  display: block;
  font-size: 9px;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  font-weight: 600;
}

.detail-summary-grid-expanded .detail-summary-item .value {
  display: block;
  font-size: 14px;
  font-weight: 700;
  color: #1e293b;
}

.detail-summary-grid-expanded .detail-summary-item .value.text-danger {
  color: #ef4444;
}

/* ===== Visits Section ===== */
.visits-summary-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 10px;
  margin-bottom: 16px;
}

.visits-summary-item {
  background: white;
  padding: 12px 14px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  gap: 10px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
}

.visits-summary-item .visits-icon {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 16px;
  flex-shrink: 0;
}

.visits-icon.total-visits { background: linear-gradient(135deg, #3b82f6, #60a5fa); }
.visits-icon.unique { background: linear-gradient(135deg, #8b5cf6, #a78bfa); }
.visits-icon.completed { background: linear-gradient(135deg, #10b981, #34d399); }
.visits-icon.active { background: linear-gradient(135deg, #f59e0b, #fbbf24); }

.visits-summary-item .label {
  display: block;
  font-size: 9px;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.visits-summary-item .value {
  display: block;
  font-size: 16px;
  font-weight: 700;
  color: #1e293b;
}

/* Visits Detail Grid */
.visits-detail-grid {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.visits-stats-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 10px;
}

.visits-stat-card {
  background: #f8fafc;
  padding: 10px 14px;
  border-radius: 8px;
  text-align: center;
}

.visits-stat-label {
  display: block;
  font-size: 10px;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.visits-stat-value {
  display: block;
  font-size: 20px;
  font-weight: 700;
  color: #1e293b;
}

/* Action Type Breakdown */
.visits-action-breakdown {
  background: white;
  border-radius: 10px;
  padding: 14px 16px;
  border: 1px solid #e2e8f0;
}

.action-type-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 10px;
}

.action-type-card {
  background: #f8fafc;
  border-radius: 8px;
  padding: 8px 12px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.action-type-name {
  font-size: 11px;
  font-weight: 600;
  color: #475569;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.action-type-count {
  font-size: 18px;
  font-weight: 700;
  color: #1e293b;
}

.action-type-bar {
  height: 4px;
  background: #e2e8f0;
  border-radius: 4px;
  overflow: hidden;
  margin-top: 2px;
}

.action-type-bar-fill {
  height: 100%;
  border-radius: 4px;
  background: linear-gradient(135deg, #6366f1, #8b5cf6);
  transition: width 0.8s ease;
}

.section-title {
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  margin: 16px 0 10px;
  padding-bottom: 6px;
  border-bottom: 2px solid #e2e8f0;
}

/* ===== RESPONSIVE ===== */
@media (max-width: 1200px) {
  .period-grid {
    grid-template-columns: repeat(2, 1fr);
  }
  .modal-summary-grid {
    grid-template-columns: repeat(3, 1fr);
  }
  .view-summary-grid {
    grid-template-columns: repeat(3, 1fr);
  }
  .visits-summary-grid {
    grid-template-columns: repeat(2, 1fr);
  }
  .visits-stats-grid {
    grid-template-columns: repeat(2, 1fr);
  }
  .spiff-grid {
    grid-template-columns: repeat(3, 1fr);
  }
  .spiff-mini-grid {
    grid-template-columns: repeat(2, 1fr);
  }
  .spiff-summary-cards {
    grid-template-columns: repeat(3, 1fr);
  }
  .detail-summary-grid-expanded {
    grid-template-columns: repeat(4, 1fr);
  }
}

@media (max-width: 768px) {
  .period-grid {
    grid-template-columns: 1fr;
  }
  .main-cards-grid {
    grid-template-columns: 1fr;
  }
  .modal-summary-grid {
    grid-template-columns: repeat(2, 1fr);
  }
  .view-summary-grid {
    grid-template-columns: repeat(2, 1fr);
  }
  .detail-summary-grid {
    grid-template-columns: repeat(2, 1fr);
  }
  .visits-summary-grid {
    grid-template-columns: 1fr;
  }
  .visits-stats-grid {
    grid-template-columns: 1fr 1fr;
  }
  .header-content {
    flex-direction: column;
    gap: 12px;
    align-items: flex-start;
  }
  .header-right {
    width: 100%;
    justify-content: flex-start;
  }
  .filters-row {
    flex-direction: column;
  }
  .filter-group {
    min-width: 100%;
  }
  .payment-methods-grid {
    grid-template-columns: 1fr 1fr;
  }
  .flip-card {
    height: 420px;
  }
  .flip-detail-grid {
    grid-template-columns: 1fr 1fr;
  }
  .flip-charts-layout {
    grid-template-columns: 1fr;
  }
  .flip-chart-wrapper {
    min-height: 60px;
  }
  .trend-chart-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
  .trend-chart-wrapper {
    height: 180px;
  }
  .flip-chart-stats.payment-stats {
    max-height: 40px;
  }
  .action-type-grid {
    grid-template-columns: 1fr 1fr;
  }
  .spiff-grid {
    grid-template-columns: repeat(3, 1fr);
  }
  .spiff-mini-grid {
    grid-template-columns: repeat(2, 1fr);
  }
  .spiff-summary-cards {
    grid-template-columns: repeat(2, 1fr);
  }
  .detail-summary-grid-expanded {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media (max-width: 576px) {
  .period-grid {
    grid-template-columns: 1fr;
  }
  .modal-summary-grid {
    grid-template-columns: 1fr;
  }
  .view-summary-grid {
    grid-template-columns: 1fr;
  }
  .detail-summary-grid {
    grid-template-columns: 1fr;
  }
  .detail-summary-grid-expanded {
    grid-template-columns: repeat(2, 1fr);
  }
  .payment-methods-grid {
    grid-template-columns: 1fr;
  }
  .visits-stats-grid {
    grid-template-columns: 1fr;
  }
  .action-type-grid {
    grid-template-columns: 1fr;
  }
  .dashboard-header {
    padding: 16px;
  }
  .flip-card {
    height: 460px;
  }
  .flip-detail-grid {
    grid-template-columns: 1fr;
  }
  .flip-stat-value {
    font-size: 20px;
  }
  .flip-detail-value {
    font-size: 13px;
  }
  .trend-chart-wrapper {
    height: 160px;
  }
  .flip-chart {
    max-height: 90px;
  }
  .flip-chart-stats.payment-stats {
    max-height: 35px;
  }
  .stats-label-item {
    font-size: 7px;
  }
  .stats-dot {
    width: 6px;
    height: 6px;
  }
  .stats-label-value {
    font-size: 7px;
  }
  .modal-summary-grid {
    grid-template-columns: 1fr 1fr;
  }
  .visits-summary-grid {
    grid-template-columns: 1fr 1fr;
  }
  .spiff-grid {
    grid-template-columns: repeat(2, 1fr);
  }
  .spiff-mini-grid {
    grid-template-columns: 1fr 1fr;
  }
  .spiff-summary-cards {
    grid-template-columns: repeat(2, 1fr);
  }
}
</style>


// WEBPACK FOOTER //
// src/components/Reports/SalesReportDashboard.vue